// src/components/dashboards/COEDashboard.jsx
// Controller of Examinations — Full Dashboard
// Design: white cards · black text · rgb(49,154,185) accent · Tailwind CSS

import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../context/AuthContext';
import DashboardLayout from '../shared/DashboardLayout';
import BookletViewer from '../shared/BookletViewer';
import { coeAPI, departmentAPI, coeEthicsAPI } from '../../services/api';
import Calendar from '../shared/Calendar';
import { notify, confirmDialog } from '../../utils/notify';


// ── Design tokens ─────────────────────────────────────────────────────────────
const ACCENT   = 'rgb(49,154,185)';
const ACCENT_L = 'rgba(49,154,185,0.12)';
const ACCENT_B = 'rgba(49,154,185,0.25)';

// Helper to resolve absolute media URLs
const getFullUrl = (url) => {
  if (!url) return '';
  if (url.startsWith('http://') || url.startsWith('https://')) return url;
  const backendBase = (process.env.REACT_APP_API_URL || 'http://localhost:8000/api').replace(/\/api\/?$/, '');
  return `${backendBase}${url}`;
};

// ── MBBS year → department codes ──────────────────────────────────────────────
const MBBS_YEAR_DEPTS = {
  1: ['ANAT', 'PHYS', 'BIOC'],
  2: ['MICR', 'PATH', 'PHAR'],
  3: ['FMED', 'COMM'],
  4: ['MED', 'SURG', 'OBGYN', 'PED', 'ENT', 'OPHT'],
};
const PG_YEARS = [1, 2, 3];

const EXAM_TYPES = [
  { value: 'UNIVERSITY', label: 'University Exam' },
  { value: 'INTERNAL',   label: 'Internal Exam' },
];

const SUB_TYPES = [
  { value: 'THEORY',    label: 'Theory' },
  { value: 'PRACTICAL', label: 'Practical' },
];

const EXAM_TYPE_COLORS = {
  UNIVERSITY: { bg: '#fee2e2', c: '#991b1b' },
  INTERNAL:   { bg: '#fef3c7', c: '#92400e' },
  MAIN:       { bg: '#dbeafe', c: '#1d4ed8' },
  PRACTICAL:  { bg: '#dcfce7', c: '#166534' },
  OTHER:      { bg: '#f3f4f6', c: '#374151' },
};

const Q_TYPES = [
  { value: 'MCQ',      label: 'Multiple Choice (MCQ)' },
  { value: 'SHORT',    label: 'Short Answer' },
  { value: 'LONG',     label: 'Long Answer' },
  { value: 'ESSAY',    label: 'Essay' },
  { value: 'FILL',     label: 'Fill in the Blanks' },
  { value: 'MATCH',    label: 'Match the Following' },
  { value: 'TF',       label: 'True or False' },
  { value: 'CLINICAL', label: 'Clinical Case Based' },
];

// ── Shared helpers ────────────────────────────────────────────────────────────
const fmtDate = (d) =>
  d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';

const fmtTime = (t) => {
  if (!t) return '—';
  const [h, m] = t.split(':');
  const hr = parseInt(h);
  return `${hr % 12 || 12}:${m} ${hr >= 12 ? 'PM' : 'AM'}`;
};

// ── StatCard ──────────────────────────────────────────────────────────────────
function StatCard({ label, value, sub, icon, accent }) {
  return (
    <div style={{
      background: '#fff',
      border: '1px solid #e5e7eb',
      borderTop: `3px solid ${accent || ACCENT}`,
      borderRadius: 12,
      padding: '18px 20px',
      display: 'flex',
      flexDirection: 'column',
      gap: 6,
      minWidth: 0,
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <span style={{ fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '.05em' }}>{label}</span>
        <span style={{ fontSize: 20 }}>{icon}</span>
      </div>
      <div style={{ fontSize: 28, fontWeight: 800, color: '#0f172a', lineHeight: 1 }}>{value}</div>
      {sub && <div style={{ fontSize: 11, color: '#94a3b8' }}>{sub}</div>}
    </div>
  );
}

// ── Accent button / ghost button ──────────────────────────────────────────────
const Btn = ({ children, onClick, disabled, ghost, danger, small }) => (
  <button
    onClick={onClick}
    disabled={disabled}
    style={{
      padding: small ? '5px 12px' : '8px 18px',
      borderRadius: 8,
      border: ghost ? `1px solid ${ACCENT}` : danger ? '1px solid #fca5a5' : 'none',
      background: ghost ? '#fff' : danger ? '#fee2e2' : ACCENT,
      color: ghost ? ACCENT : danger ? '#dc2626' : '#fff',
      fontWeight: 700,
      fontSize: small ? 11 : 13,
      cursor: disabled ? 'not-allowed' : 'pointer',
      opacity: disabled ? 0.55 : 1,
      fontFamily: 'inherit',
      transition: 'opacity .15s',
      whiteSpace: 'nowrap',
    }}
  >
    {children}
  </button>
);

// ── Input / Select helpers ────────────────────────────────────────────────────
const inputStyle = {
  width: '100%',
  padding: '8px 10px',
  border: '1px solid #e2e8f0',
  borderRadius: 8,
  fontSize: 13,
  fontFamily: 'inherit',
  color: '#0f172a',
  background: '#fff',
  boxSizing: 'border-box',
  outline: 'none',
};

const labelStyle = {
  display: 'block',
  fontSize: 11,
  fontWeight: 700,
  color: '#64748b',
  textTransform: 'uppercase',
  letterSpacing: '.04em',
  marginBottom: 4,
};

function Field({ label, children }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column' }}>
      {label && <label style={labelStyle}>{label}</label>}
      {children}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// TAB: Overview (Dashboard)
// ══════════════════════════════════════════════════════════════════════════════
function OverviewTab({ stats, onNavigate }) {
  const cards = [
    { label: 'Timetables',       value: stats.total_timetables  ?? 0, icon: '📅', sub: 'Published exam calendars' },
    { label: 'Exam Schedules',   value: stats.total_schedules   ?? 0, icon: '📋', sub: 'Individual exam slots' },
    { label: 'Upcoming Exams',   value: stats.upcoming_exams    ?? 0, icon: '⏳', sub: 'Yet to be conducted',    accent: ACCENT },
    { label: 'Completed Exams',  value: stats.completed_exams   ?? 0, icon: '✅', sub: 'Past exam date',         accent: '#10b981' },
    { label: 'Students Assigned',value: stats.total_assigned    ?? 0, icon: '🎓', sub: 'Total assignments' },
    { label: 'Pending Sheets',   value: stats.pending_sheets    ?? 0, icon: '📄', sub: 'Answer sheets awaited',  accent: '#f59e0b' },
    { label: 'Practical / Viva', value: stats.practical_exams   ?? 0, icon: '🔬', sub: 'Practical, Viva, Clinical' },
    { label: 'Main Exams',       value: stats.main_exams        ?? 0, icon: '📝', sub: 'Theory / Main exams' },
    { label: 'Question Papers',  value: stats.question_papers   ?? 0, icon: '🗂',  sub: 'Papers created' },
  ];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
      <div style={{
        background: `linear-gradient(135deg, ${ACCENT} 0%, rgb(30,120,150) 100%)`,
        borderRadius: 16,
        padding: '24px 28px',
        color: '#fff',
      }}>
        <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.08em', opacity: .8 }}>
          Controller of Examinations
        </div>
        <div style={{ fontSize: 24, fontWeight: 800, marginTop: 4 }}>Examination Management Portal</div>
        <div style={{ fontSize: 13, opacity: .8, marginTop: 6 }}>
          Manage timetables, schedules, student assignments, answer sheets, and question papers from one place.
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 14 }}>
        {cards.map(c => <StatCard key={c.label} {...c} />)}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 14 }}>
        {[
          { label: 'Create Timetable', icon: '📅', desc: 'Set up a new exam calendar for MBBS or PG students.', tab: 'timetables' },
          { label: 'Add Exam Schedule', icon: '📋', desc: 'Schedule individual exam slots with date, time, and venue.', tab: 'schedules' },
          { label: 'Assign Students', icon: '🎓', desc: 'Select and assign eligible students to exam schedules.', tab: 'students' },
          { label: 'Build Question Paper', icon: '📝', desc: 'Compose sections and questions, then export to PDF.', tab: 'question-papers' },
        ].map(q => (
          <button key={q.label} onClick={() => onNavigate(q.tab)}
            style={{ background: '#fff', border: `1px solid #e5e7eb`, borderRadius: 12, padding: '18px 20px', textAlign: 'left', cursor: 'pointer', fontFamily: 'inherit', transition: 'border-color .15s' }}
            onMouseEnter={e => e.currentTarget.style.borderColor = ACCENT}
            onMouseLeave={e => e.currentTarget.style.borderColor = '#e5e7eb'}
          >
            <div style={{ fontSize: 28, marginBottom: 8 }}>{q.icon}</div>
            <div style={{ fontWeight: 700, fontSize: 14, color: '#0f172a' }}>{q.label}</div>
            <div style={{ fontSize: 12, color: '#64748b', marginTop: 4 }}>{q.desc}</div>
          </button>
        ))}
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// TAB: Timetables
// ══════════════════════════════════════════════════════════════════════════════
function TimetablesTab() {
  const [timetables, setTimetables] = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [showForm,   setShowForm]   = useState(false);
  const [form, setForm] = useState({ title: '', course: 'MBBS', year: 1, academic_year: '', description: '' });
  const [saving, setSaving] = useState(false);

  const load = () => {
    setLoading(true);
    coeAPI.getTimetables()
      .then(d => setTimetables(Array.isArray(d) ? d : (d.results || [])))
      .catch(() => setTimetables([]))
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  const handleCreate = async () => {
    if (!form.title.trim() || !form.academic_year.trim()) return;
    setSaving(true);
    try {
      await coeAPI.createTimetable(form);
      setShowForm(false);
      setForm({ title: '', course: 'MBBS', year: 1, academic_year: '', description: '' });
      load();
    } catch (e) {
      notify.error(e?.response?.data?.error || 'Failed to create timetable.');
    } finally { setSaving(false); }
  };

  const handlePublish = async (tt) => {
    try {
      await coeAPI.updateTimetable(tt.id, { is_published: !tt.is_published });
      load();
    } catch { notify.error('Failed to update.'); }
  };

  const handleDelete = async (id) => {
    if (!await confirmDialog('Delete this timetable and all its schedules?')) return;
    try { await coeAPI.deleteTimetable(id); load(); }
    catch { notify.error('Failed to delete.'); }
  };

  const yearOpts = form.course === 'MBBS'
    ? [1, 2, 3, 4].map(y => ({ value: y, label: `${['1st','2nd','3rd','4th'][y-1]} Year${y===4?' (Final)':''}` }))
    : [1, 2, 3].map(y => ({ value: y, label: `Year ${y}` }));

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 10 }}>
        <h2 style={{ margin: 0, fontSize: 18, fontWeight: 800, color: '#0f172a' }}>Examination Timetables</h2>
        <Btn onClick={() => setShowForm(p => !p)}>{showForm ? '✕ Cancel' : '+ New Timetable'}</Btn>
      </div>

      {showForm && (
        <div style={{ background: '#f8fafc', border: `1px solid ${ACCENT_B}`, borderRadius: 14, padding: 20, display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div style={{ fontWeight: 700, fontSize: 14, color: '#0f172a' }}>New Timetable</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 14 }}>
            <Field label="Timetable Title *">
              <input style={inputStyle} value={form.title} onChange={e => setForm(p => ({...p, title: e.target.value}))} placeholder="e.g. MBBS 1st Year — Nov 2025" />
            </Field>
            <Field label="Course *">
              <select style={inputStyle} value={form.course} onChange={e => setForm(p => ({...p, course: e.target.value, year: 1}))}>
                <option value="MBBS">MBBS</option>
                <option value="PG">Post Graduate (PG)</option>
              </select>
            </Field>
            <Field label="Year *">
              <select style={inputStyle} value={form.year} onChange={e => setForm(p => ({...p, year: parseInt(e.target.value)}))}>
                {yearOpts.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </Field>
            <Field label="Academic Year *">
              <input style={inputStyle} value={form.academic_year} onChange={e => setForm(p => ({...p, academic_year: e.target.value}))} placeholder="2025-26" />
            </Field>
            <Field label="Description">
              <input style={inputStyle} value={form.description} onChange={e => setForm(p => ({...p, description: e.target.value}))} placeholder="Optional notes…" />
            </Field>
          </div>
          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
            <Btn ghost onClick={() => setShowForm(false)}>Cancel</Btn>
            <Btn onClick={handleCreate} disabled={saving || !form.title.trim() || !form.academic_year.trim()}>
              {saving ? 'Creating…' : '💾 Create Timetable'}
            </Btn>
          </div>
        </div>
      )}

      {loading ? (
        <div style={{ textAlign: 'center', padding: 48, color: '#64748b' }}>Loading timetables…</div>
      ) : timetables.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 48, color: '#94a3b8', border: '2px dashed #e2e8f0', borderRadius: 12 }}>
          No timetables yet. Create one above.
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {timetables.map(tt => (
            <div key={tt.id} style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 14, padding: '16px 20px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap' }}>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 800, fontSize: 15, color: '#0f172a' }}>{tt.title}</div>
                  <div style={{ fontSize: 12, color: '#64748b', marginTop: 3 }}>
                    {tt.course_display} · {tt.year_display} · {tt.academic_year}
                  </div>
                  {tt.description && <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 2 }}>{tt.description}</div>}
                </div>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                  <span style={{ padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 700, background: tt.is_published ? '#dcfce7' : '#f3f4f6', color: tt.is_published ? '#166534' : '#64748b' }}>
                    {tt.is_published ? '✓ Published' : 'Draft'}
                  </span>
                  <Btn small ghost onClick={() => handlePublish(tt)}>
                    {tt.is_published ? 'Unpublish' : 'Publish'}
                  </Btn>
                  <Btn small danger onClick={() => handleDelete(tt.id)}>Delete</Btn>
                </div>
              </div>
              <div style={{ display: 'flex', gap: 16, marginTop: 12, flexWrap: 'wrap' }}>
                <span style={{ fontSize: 12, color: '#64748b' }}>📋 {tt.total_exams} exam(s)</span>
                <span style={{ fontSize: 12, color: '#64748b' }}>Created {fmtDate(tt.created_at)}</span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// TAB: Exam Schedules
// ══════════════════════════════════════════════════════════════════════════════
const DEPT_SUBJECTS = {
  'ANAT': ['Anatomy'],
  'PHYS': ['Physiology'],
  'BIOC': ['Biochemistry'],
  'MICR': ['Microbiology'],
  'PATH': ['Pathology'],
  'PHAR': ['Pharmacology'],
  'FMED': ['Forensic Medicine'],
  'COMM': ['Community Medicine'],
  'MED':  ['General Medicine'],
  'SURG': ['General Surgery'],
  'OBGYN': ['Obstetrics & Gynaecology'],
  'PED':  ['Paediatrics'],
  'ENT':  ['Otorhinolaryngology (ENT)'],
  'OPHT': ['Ophthalmology'],
  'ORTHO': ['Orthopaedics'],
  'DVL':   ['Dermatology, Venereology & Leprosy (DVL)'],
  'PSY':   ['Psychiatry'],
  'PULM':  ['Respiratory Medicine (Pulmonology)'],
  'ANES':  ['Anaesthesiology'],
  'CARD':  ['Cardiology'],
  'NEUR':  ['Neurology'],
  'NEPH':  ['Nephrology'],
  'GAST':  ['Gastroenterology'],
  'ENDO':  ['Endocrinology'],
  'MONCO': ['Medical Oncology'],
  'HAEM':  ['Clinical Haematology'],
  'RHEUM': ['Rheumatology'],
  'NEONA': ['Neonatology'],
  'PDSURG':['Paediatric Surgery'],
  'URO':   ['Urology'],
  'NSURG': ['Neurosurgery'],
  'PLAST': ['Plastic & Reconstructive Surgery'],
  'CTVS':  ['Cardiothoracic & Vascular Surgery (CTVS)'],
  'SONCO': ['Surgical Oncology'],
  'CCM':   ['Critical Care Medicine'],
  'RAD':   ['Radiology'],
  'EMER':  ['Emergency Medicine'],
};

function SchedulesTab() {
  const [timetables,  setTimetables]  = useState([]);
  const [departments, setDepartments] = useState([]);
  const [selectedTT,  setSelectedTT]  = useState('');
  const [schedules,   setSchedules]   = useState([]);
  const [pgStudents,  setPgStudents]  = useState([]);
  const [loading,     setLoading]     = useState(false);
  const [showForm,    setShowForm]    = useState(false);
  const [saving,      setSaving]      = useState(false);
  const [editId,      setEditId]      = useState(null);

  const blank = () => ({
    timetable: '', department: '', subject: '', exam_type: 'UNIVERSITY', sub_type: '',
    paper_number: '', paper_label: '',
    exam_date: '', start_time: '', end_time: '',
    venue: '', max_marks: 100, pass_marks: 50, duration_minutes: 180, instructions: '',
    max_students_per_day: '',
  });
  const [form, setForm] = useState(blank());

  useEffect(() => {
    coeAPI.getTimetables().then(d => setTimetables(Array.isArray(d) ? d : (d.results || []))).catch(() => {});
    departmentAPI.getDepartments().then(d => setDepartments(Array.isArray(d) ? d : [])).catch(() => {});
    coeAPI.getPGStudentDepartments().then(d => setPgStudents(d)).catch(() => {});
  }, []);

  useEffect(() => {
    if (!selectedTT) { setSchedules([]); return; }
    setLoading(true);
    coeAPI.getSchedules({ timetable_id: selectedTT })
      .then(d => setSchedules(Array.isArray(d) ? d : (d.results || [])))
      .catch(() => setSchedules([]))
      .finally(() => setLoading(false));
  }, [selectedTT]);

  // Dept filtering for the Add/Edit form based on the form's selected timetable
  const activeFormTT = timetables.find(t => String(t.id) === String(form.timetable));
  const formFilteredDepts = activeFormTT
    ? (() => {
        if (activeFormTT.course === 'MBBS') {
          const codes = MBBS_YEAR_DEPTS[activeFormTT.year] || [];
          return departments.filter(d => codes.includes(d.code));
        } else if (activeFormTT.course === 'PG') {
          // PG: only show departments where PG students are registered!
          const registeredDeptIds = pgStudents.map(p => p.department_id);
          return departments.filter(d => registeredDeptIds.includes(d.id));
        }
        return departments;
      })()
    : departments;

  // Auto-select PG student's registered department when a PG Timetable is chosen in the form
  useEffect(() => {
    if (activeFormTT && activeFormTT.course === 'PG') {
      const registeredDepts = departments.filter(d => 
        pgStudents.map(p => p.department_id).includes(d.id)
      );
      const isValidDept = registeredDepts.some(d => String(d.id) === String(form.department));
      if (registeredDepts.length > 0 && !isValidDept) {
        setForm(p => ({
          ...p,
          department: String(registeredDepts[0].id)
        }));
      }
    }
  }, [form.timetable, pgStudents, departments, form.department, activeFormTT]);

  const selectedDeptObj = departments.find(d => String(d.id) === String(form.department));
  const subjectsForSelectedDept = selectedDeptObj
    ? (DEPT_SUBJECTS[selectedDeptObj.code] || [selectedDeptObj.name])
    : [];

  // Auto-select the first subject if the current subject is not in the filtered subjects list
  useEffect(() => {
    if (subjectsForSelectedDept.length > 0) {
      if (!subjectsForSelectedDept.includes(form.subject)) {
        setForm(p => ({ ...p, subject: subjectsForSelectedDept[0] }));
      }
    } else if (form.subject !== '') {
      setForm(p => ({ ...p, subject: '' }));
    }
  }, [form.department, subjectsForSelectedDept, form.subject]);

  const handleSave = async () => {
    if (!form.timetable || !form.department || !form.subject || !form.exam_date) {
      notify.error('Please fill all required fields.'); return;
    }
    if (!form.sub_type) { notify.error('Please select a Sub Type (Theory or Practical).'); return; }
    if (form.sub_type === 'THEORY' && !form.paper_number) {
      notify.success('Paper Number is required for Theory exams.'); return;
    }
    setSaving(true);
    const payload = {
      ...form,
      max_marks: parseInt(form.max_marks) || 100,
      pass_marks: parseInt(form.pass_marks) || 50,
      paper_number: form.sub_type === 'PRACTICAL' ? null : (parseInt(form.paper_number) || null),
      max_students_per_day: form.max_students_per_day ? parseInt(form.max_students_per_day) : null,
    };
    try {
      if (editId) {
        await coeAPI.updateSchedule(editId, payload);
      } else {
        await coeAPI.createSchedule(payload);
      }
      setShowForm(false);
      setEditId(null);
      setForm(blank());
      if (selectedTT) {
        const d = await coeAPI.getSchedules({ timetable_id: selectedTT });
        setSchedules(Array.isArray(d) ? d : (d.results || []));
      }
    } catch (e) {
      notify.error(e?.response?.data?.error || JSON.stringify(e?.response?.data) || 'Failed to save.');
    } finally { setSaving(false); }
  };

  const startEdit = (s) => {
    setForm({
      timetable: s.timetable, department: s.department, subject: s.subject,
      exam_type: s.exam_type, sub_type: s.sub_type || '',
      paper_number: s.paper_number || '', paper_label: s.paper_label || '',
      exam_date: s.exam_date, start_time: s.start_time, end_time: s.end_time,
      venue: s.venue || '',
      max_marks: parseInt(s.max_marks) || 100,
      pass_marks: parseInt(s.pass_marks) || 50,
      duration_minutes: s.duration_minutes, instructions: s.instructions || '',
      max_students_per_day: s.max_students_per_day !== null && s.max_students_per_day !== undefined ? s.max_students_per_day : '',
    });
    setEditId(s.id);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!await confirmDialog('Delete this schedule?')) return;
    try {
      await coeAPI.deleteSchedule(id);
      setSchedules(p => p.filter(s => s.id !== id));
    } catch { notify.error('Failed to delete.'); }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 10 }}>
        <h2 style={{ margin: 0, fontSize: 18, fontWeight: 800, color: '#0f172a' }}>Exam Schedules</h2>
        <Btn onClick={() => { setShowForm(p => !p); setEditId(null); setForm(blank()); }}>
          {showForm && !editId ? '✕ Cancel' : '+ Add Schedule'}
        </Btn>
      </div>

      {/* Timetable filter */}
      <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
        <label style={labelStyle}>Filter by Timetable:</label>
        <select style={{ ...inputStyle, maxWidth: 300 }} value={selectedTT} onChange={e => setSelectedTT(e.target.value)}>
          <option value="">— All Timetables —</option>
          {timetables.map(t => <option key={t.id} value={t.id}>{t.title} ({t.course_display} · {t.year_display})</option>)}
        </select>
      </div>

      {/* Schedule form */}
      {showForm && (
        <div style={{ background: '#f8fafc', border: `1px solid ${ACCENT_B}`, borderRadius: 14, padding: 20, display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div style={{ fontWeight: 700, fontSize: 14, color: '#0f172a' }}>{editId ? 'Edit Schedule' : 'New Exam Schedule'}</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 14 }}>
            <Field label="Timetable *">
              <select style={inputStyle} value={form.timetable} onChange={e => setForm(p => ({...p, timetable: e.target.value}))}>
                <option value="">Select…</option>
                {timetables.map(t => <option key={t.id} value={t.id}>{t.title}</option>)}
              </select>
            </Field>
            <Field label="Department *">
              <select style={inputStyle} value={form.department} onChange={e => setForm(p => ({...p, department: e.target.value}))}>
                <option value="">Select…</option>
                {formFilteredDepts.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
              </select>
            </Field>
            <Field label="Subject *">
              <select style={inputStyle} value={form.subject} onChange={e => setForm(p => ({...p, subject: e.target.value}))}>
                <option value="">Select Subject…</option>
                {subjectsForSelectedDept.map(sub => <option key={sub} value={sub}>{sub}</option>)}
              </select>
            </Field>
            <Field label="Exam Type *">
              <select style={inputStyle} value={form.exam_type} onChange={e => setForm(p => ({...p, exam_type: e.target.value, sub_type: '', paper_number: ''})) }>
                {EXAM_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
            </Field>
            <Field label="Sub Type *">
              <select style={inputStyle} value={form.sub_type} onChange={e => setForm(p => ({...p, sub_type: e.target.value, paper_number: ''})) }>
                <option value="">— Select —</option>
                {SUB_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
            </Field>
            {form.sub_type === 'THEORY' && (
              <Field label="Paper Number *">
                <input style={inputStyle} type="number" min="1" step="1" value={form.paper_number}
                  onChange={e => setForm(p => ({...p, paper_number: parseInt(e.target.value) || ''}))}
                  placeholder="1" />
              </Field>
            )}
            {form.sub_type === 'THEORY' && (
              <Field label="Paper Label">
                <input style={inputStyle} value={form.paper_label} onChange={e => setForm(p => ({...p, paper_label: e.target.value}))} placeholder="e.g. Anatomy Paper I" />
              </Field>
            )}
            <Field label="Exam Date *">
              <input style={inputStyle} type="date" value={form.exam_date} onChange={e => setForm(p => ({...p, exam_date: e.target.value}))} />
            </Field>
            <Field label="Start Time *">
              <input style={inputStyle} type="time" value={form.start_time} onChange={e => setForm(p => ({...p, start_time: e.target.value}))} />
            </Field>
            <Field label="End Time *">
              <input style={inputStyle} type="time" value={form.end_time} onChange={e => setForm(p => ({...p, end_time: e.target.value}))} />
            </Field>
            <Field label="Venue / Hall">
              <input style={inputStyle} value={form.venue} onChange={e => setForm(p => ({...p, venue: e.target.value}))} placeholder="Exam Hall A" />
            </Field>
            <Field label="Max Marks">
              <input style={inputStyle} type="number" step="1" value={form.max_marks} onChange={e => setForm(p => ({...p, max_marks: parseInt(e.target.value)||100}))} />
            </Field>
            <Field label="Pass Marks">
              <input style={inputStyle} type="number" step="1" value={form.pass_marks} onChange={e => setForm(p => ({...p, pass_marks: parseInt(e.target.value)||50}))} />
            </Field>
            <Field label="Duration (mins)">
              <input style={inputStyle} type="number" value={form.duration_minutes} onChange={e => setForm(p => ({...p, duration_minutes: parseInt(e.target.value)||180}))} />
            </Field>
            {(form.sub_type === 'PRACTICAL' || form.exam_type === 'INTERNAL') && (
              <Field label="Max Students / Day">
                <input style={inputStyle} type="number" min="1" step="1" value={form.max_students_per_day}
                  onChange={e => setForm(p => ({...p, max_students_per_day: e.target.value}))}
                  placeholder="Unlimited" />
              </Field>
            )}
          </div>
          <Field label="Instructions">
            <textarea style={{ ...inputStyle, resize: 'vertical', minHeight: 60 }} value={form.instructions} onChange={e => setForm(p => ({...p, instructions: e.target.value}))} placeholder="Optional exam instructions…" />
          </Field>
          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
            <Btn ghost onClick={() => { setShowForm(false); setEditId(null); setForm(blank()); }}>Cancel</Btn>
            <Btn onClick={handleSave} disabled={saving}>{saving ? 'Saving…' : editId ? '💾 Update' : '💾 Create Schedule'}</Btn>
          </div>
        </div>
      )}

      {/* Schedules list */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: 40, color: '#64748b' }}>Loading…</div>
      ) : schedules.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 48, color: '#94a3b8', border: '2px dashed #e2e8f0', borderRadius: 12 }}>
          {selectedTT ? 'No schedules for this timetable.' : 'Select a timetable to view its schedules.'}
        </div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 700, background: '#fff', borderRadius: 12, overflow: 'hidden', border: '1px solid #e5e7eb' }}>
            <thead>
              <tr style={{ background: '#f8fafc', borderBottom: '1px solid #e5e7eb' }}>
                {['Subject / Paper', 'Department', 'Type', 'Date & Time', 'Venue', 'Marks', 'Students', 'Actions'].map(h => (
                  <th key={h} style={{ padding: '10px 14px', textAlign: 'left', fontSize: 10, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '.04em', whiteSpace: 'nowrap' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {schedules.map((s, i) => {
                const tc = EXAM_TYPE_COLORS[s.exam_type] || EXAM_TYPE_COLORS.OTHER;
                return (
                  <tr key={s.id} style={{ borderBottom: '1px solid #f1f5f9', background: i % 2 === 0 ? '#fff' : '#fafafa' }}>
                    <td style={{ padding: '10px 14px' }}>
                      <div style={{ fontWeight: 700, fontSize: 13, color: '#0f172a' }}>{s.paper_label || s.subject}</div>
                      {s.paper_label && <div style={{ fontSize: 11, color: '#94a3b8' }}>{s.subject}</div>}
                    </td>
                    <td style={{ padding: '10px 14px', fontSize: 12, color: '#374151' }}>{s.department_name}</td>
                    <td style={{ padding: '10px 14px' }}>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                        <span style={{ padding: '2px 8px', borderRadius: 99, fontSize: 10, fontWeight: 700, background: tc.bg, color: tc.c }}>{s.exam_type_display}</span>
                        {s.sub_type && <span style={{ padding: '2px 8px', borderRadius: 99, fontSize: 10, fontWeight: 600, background: s.sub_type === 'THEORY' ? '#dbeafe' : '#dcfce7', color: s.sub_type === 'THEORY' ? '#1d4ed8' : '#166534' }}>{s.sub_type_display || s.sub_type}</span>}
                      </div>
                    </td>
                    <td style={{ padding: '10px 14px', fontSize: 12, color: '#374151', whiteSpace: 'nowrap' }}>
                      <div style={{ fontWeight: 600 }}>{fmtDate(s.exam_date)}</div>
                      <div style={{ color: '#64748b' }}>{fmtTime(s.start_time)} – {fmtTime(s.end_time)}</div>
                    </td>
                    <td style={{ padding: '10px 14px', fontSize: 12, color: '#374151' }}>{s.venue || '—'}</td>
                    <td style={{ padding: '10px 14px', fontSize: 12, color: '#374151', whiteSpace: 'nowrap' }}>
                      {s.max_marks} / Pass: {s.pass_marks}
                    </td>
                    <td style={{ padding: '10px 14px', fontSize: 12, color: '#374151' }}>
                      {s.student_count}
                      {s.max_students_per_day && (
                        <div style={{ fontSize: 10, color: '#e11d48', fontWeight: 600 }}>Max/day: {s.max_students_per_day}</div>
                      )}
                    </td>
                    <td style={{ padding: '10px 14px' }}>
                      <div style={{ display: 'flex', gap: 6 }}>
                        <Btn small ghost onClick={() => startEdit(s)}>Edit</Btn>
                        <Btn small danger onClick={() => handleDelete(s.id)}>Del</Btn>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// TAB: Student Assignment
// ══════════════════════════════════════════════════════════════════════════════
function StudentsTab() {
  const [timetables,   setTimetables]   = useState([]);
  const [selectedTT,   setSelectedTT]   = useState('');
  const [schedules,    setSchedules]    = useState([]);
  const [selectedSch,  setSelectedSch]  = useState('');
  const [eligible,     setEligible]     = useState([]);
  const [assigned,     setAssigned]     = useState([]);
  const [searchQ,      setSearchQ]      = useState('');
  const [selected,     setSelected]     = useState(new Set());
  const [loading,      setLoading]      = useState(false);
  const [assigning,    setAssigning]    = useState(false);

  useEffect(() => {
    coeAPI.getTimetables()
      .then(d => setTimetables(Array.isArray(d) ? d : (d?.results ?? [])))
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!selectedTT) { setSchedules([]); setSelectedSch(''); return; }
    coeAPI.getSchedules({ timetable_id: selectedTT })
      .then(d => setSchedules(Array.isArray(d) ? d : (d?.results ?? [])))
      .catch(() => setSchedules([]));
  }, [selectedTT]);

  useEffect(() => {
    if (!selectedSch) { setEligible([]); setAssigned([]); setSelected(new Set()); return; }
    setLoading(true);
    Promise.all([
      coeAPI.getEligibleStudents(selectedSch, searchQ),
      coeAPI.getScheduleStudents(selectedSch),
    ])
      .then(([el, as]) => { setEligible(Array.isArray(el) ? el : []); setAssigned(Array.isArray(as) ? as : []); })
      .catch(() => { setEligible([]); setAssigned([]); })
      .finally(() => setLoading(false));
  }, [selectedSch]);

  const searchTimer = useRef(null);
  const handleSearch = (q) => {
    setSearchQ(q);
    clearTimeout(searchTimer.current);
    if (!selectedSch) return;
    searchTimer.current = setTimeout(() => {
      coeAPI.getEligibleStudents(selectedSch, q)
        .then(d => setEligible(Array.isArray(d) ? d : []))
        .catch(() => {});
    }, 350);
  };

  const unassigned = eligible.filter(s => !s.assigned);

  const toggleSelect = (id) => {
    setSelected(p => { const n = new Set(p); n.has(id) ? n.delete(id) : n.add(id); return n; });
  };
  const selectAll = () => setSelected(new Set(unassigned.map(s => s.id)));
  const clearAll  = () => setSelected(new Set());

  const handleAssign = async () => {
    if (selected.size === 0) return;
    setAssigning(true);
    try {
      await coeAPI.assignStudents(selectedSch, [...selected]);
      setSelected(new Set());
      const [el, as] = await Promise.all([
        coeAPI.getEligibleStudents(selectedSch, searchQ),
        coeAPI.getScheduleStudents(selectedSch),
      ]);
      setEligible(Array.isArray(el) ? el : []);
      setAssigned(Array.isArray(as) ? as : []);
    } catch (e) {
      notify.error(e?.response?.data?.error || 'Failed to assign students.');
    } finally { setAssigning(false); }
  };

  const handleRemove = async (assignmentId) => {
    if (!await confirmDialog('Remove this student from the exam?')) return;
    try {
      await coeAPI.removeAssignment(assignmentId);
      const [el, as] = await Promise.all([
        coeAPI.getEligibleStudents(selectedSch, searchQ),
        coeAPI.getScheduleStudents(selectedSch),
      ]);
      setEligible(Array.isArray(el) ? el : []);
      setAssigned(Array.isArray(as) ? as : []);
    } catch { notify.error('Failed to remove.'); }
  };

  const activeSch = schedules.find(s => String(s.id) === String(selectedSch));

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
      <h2 style={{ margin: 0, fontSize: 18, fontWeight: 800, color: '#0f172a' }}>Student Assignment</h2>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 14 }}>
        <Field label="Select Timetable">
          <select style={inputStyle} value={selectedTT} onChange={e => setSelectedTT(e.target.value)}>
            <option value="">— Choose Timetable —</option>
            {timetables.map(t => <option key={t.id} value={t.id}>{t.title}</option>)}
          </select>
        </Field>
        <Field label="Select Exam Schedule">
          <select style={inputStyle} value={selectedSch} onChange={e => setSelectedSch(e.target.value)} disabled={!selectedTT}>
            <option value="">— Choose Schedule —</option>
            {schedules.map(s => {
              const examTypeSubStr = s.exam_type === 'UNIVERSITY'
                ? (s.sub_type === 'THEORY' ? 'University Theory' : 'University Practical')
                : (s.exam_type === 'INTERNAL'
                  ? (s.sub_type === 'THEORY' ? 'Internal Theory' : 'Internal Practical')
                  : `${s.exam_type_display} ${s.sub_type_display}`);
              return (
                <option key={s.id} value={s.id}>
                  {s.paper_label || s.subject} ({examTypeSubStr}) — {fmtDate(s.exam_date)}
                </option>
              );
            })}
          </select>
        </Field>
      </div>

      {activeSch && (
        <div style={{ background: ACCENT_L, border: `1px solid ${ACCENT_B}`, borderRadius: 10, padding: '12px 16px', fontSize: 13, display: 'flex', gap: 20, flexWrap: 'wrap' }}>
          <span style={{ fontWeight: 700, color: '#0f172a' }}>{activeSch.paper_label || activeSch.subject}</span>
          <span style={{ color: '#64748b' }}>📅 {fmtDate(activeSch.exam_date)}</span>
          <span style={{ color: '#64748b' }}>⏰ {fmtTime(activeSch.start_time)}</span>
          <span style={{ color: '#64748b' }}>📍 {activeSch.venue || 'TBA'}</span>
          <span style={{ fontWeight: 700, color: ACCENT }}>✓ {assigned.length} assigned</span>
        </div>
      )}

      {selectedSch && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 18 }}>
          {/* Left: Eligible students */}
          <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, overflow: 'hidden' }}>
            <div style={{ padding: '12px 16px', borderBottom: '1px solid #f1f5f9', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
              <div>
                <div style={{ fontWeight: 700, fontSize: 13, color: '#0f172a' }}>Eligible Students</div>
                <div style={{ fontSize: 11, color: '#64748b' }}>{unassigned.length} not yet assigned</div>
              </div>
              <div style={{ display: 'flex', gap: 6 }}>
                <Btn small ghost onClick={selectAll}>Select All</Btn>
                <Btn small ghost onClick={clearAll}>Clear</Btn>
              </div>
            </div>
            <div style={{ padding: '10px 14px', borderBottom: '1px solid #f1f5f9' }}>
              <input style={inputStyle} placeholder="Search students…" value={searchQ} onChange={e => handleSearch(e.target.value)} />
            </div>
            {loading ? (
              <div style={{ padding: 32, textAlign: 'center', color: '#94a3b8' }}>Loading…</div>
            ) : (
              <div style={{ maxHeight: 360, overflowY: 'auto' }}>
                {unassigned.length === 0 ? (
                  <div style={{ padding: 24, textAlign: 'center', color: '#94a3b8', fontSize: 13 }}>All eligible students assigned.</div>
                ) : unassigned.map(s => (
                  <div key={s.id} onClick={() => toggleSelect(s.id)}
                    style={{ padding: '10px 16px', display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', background: selected.has(s.id) ? ACCENT_L : '#fff', borderBottom: '1px solid #f8fafc', transition: 'background .1s' }}>
                    <input type="checkbox" checked={selected.has(s.id)} readOnly style={{ accentColor: ACCENT, width: 15, height: 15 }} />
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 600, fontSize: 13, color: '#0f172a' }}>{s.full_name}</div>
                      <div style={{ fontSize: 11, color: '#64748b' }}>{s.student_id || '—'} · {s.course}</div>
                    </div>
                  </div>
                ))}
              </div>
            )}
            <div style={{ padding: '10px 14px', borderTop: '1px solid #f1f5f9', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontSize: 12, color: '#64748b' }}>{selected.size} selected</span>
              <Btn onClick={handleAssign} disabled={selected.size === 0 || assigning} small>
                {assigning ? 'Assigning…' : `Assign ${selected.size > 0 ? selected.size : ''}`}
              </Btn>
            </div>
          </div>

          {/* Right: Assigned students */}
          <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, overflow: 'hidden' }}>
            <div style={{ padding: '12px 16px', borderBottom: '1px solid #f1f5f9' }}>
              <div style={{ fontWeight: 700, fontSize: 13, color: '#0f172a' }}>Assigned Students ({assigned.length})</div>
            </div>
            <div style={{ maxHeight: 420, overflowY: 'auto' }}>
              {assigned.length === 0 ? (
                <div style={{ padding: 32, textAlign: 'center', color: '#94a3b8', fontSize: 13 }}>No students assigned yet.</div>
              ) : assigned.map((a, i) => (
                <div key={a.id} style={{ padding: '10px 16px', display: 'flex', alignItems: 'center', gap: 10, borderBottom: '1px solid #f8fafc', background: i % 2 === 0 ? '#fff' : '#fafafa' }}>
                  <div style={{ width: 28, height: 28, borderRadius: 6, background: ACCENT_L, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 700, color: ACCENT }}>
                    {String(i + 1).padStart(2, '0')}
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 600, fontSize: 13, color: '#0f172a' }}>{a.student_name}</div>
                    <div style={{ fontSize: 11, color: '#64748b' }}>{a.student_id_num || '—'}</div>
                  </div>
                  <span style={{ fontSize: 10, padding: '2px 8px', borderRadius: 99, fontWeight: 700, background: a.attendance === 'PRESENT' ? '#dcfce7' : a.attendance === 'ABSENT' ? '#fee2e2' : '#f3f4f6', color: a.attendance === 'PRESENT' ? '#166534' : a.attendance === 'ABSENT' ? '#dc2626' : '#64748b' }}>
                    {a.attendance_display}
                  </span>
                  <Btn small danger onClick={() => handleRemove(a.id)}>✕</Btn>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Helper component to render each sheet with zoom, rotate, reset controls
function SheetPreviewCard({ sheet, index, isPdf, ACCENT, getFullUrl }) {
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);

  const handleZoomIn = () => setZoom(prev => Math.min(prev + 0.2, 3));
  const handleZoomOut = () => setZoom(prev => Math.max(prev - 0.2, 0.5));
  const handleReset = () => {
    setZoom(1);
    setRotation(0);
  };
  const handleRotate = () => setRotation(prev => (prev + 90) % 360);

  return (
    <div
      style={{
        background: '#fff',
        borderRadius: 12,
        border: '1px solid #e2e8f0',
        overflow: 'hidden',
        boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05)',
      }}
    >
      <div style={{
        padding: '12px 16px',
        background: '#f8fafc',
        borderBottom: '1px solid #e2e8f0',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: 8,
      }}>
        <span style={{ fontSize: 13, fontWeight: 700, color: '#1e293b' }}>
          Sheet #{index + 1}: {sheet.file_name || `Document ${sheet.id}`}
        </span>
        
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          {!isPdf && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 4, background: '#f1f5f9', padding: '2px 6px', borderRadius: 8 }}>
              <button
                onClick={handleZoomOut}
                title="Zoom Out"
                style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '2px 4px', fontSize: 11, fontWeight: 'bold', color: '#475569' }}
              >
                ➖
              </button>
              <span style={{ fontSize: 10, fontWeight: 'bold', color: '#475569', minWidth: 35, textAlign: 'center' }}>
                {Math.round(zoom * 100)}%
              </span>
              <button
                onClick={handleZoomIn}
                title="Zoom In"
                style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '2px 4px', fontSize: 11, fontWeight: 'bold', color: '#475569' }}
              >
                ➕
              </button>
              <button
                onClick={handleRotate}
                title="Rotate 90°"
                style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '2px 4px', fontSize: 12, color: '#475569' }}
              >
                ⟳
              </button>
              <button
                onClick={handleReset}
                title="Reset View"
                style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '2px 4px', fontSize: 10, color: '#64748b', fontWeight: '600' }}
              >
                Reset
              </button>
            </div>
          )}
          <a
            href={getFullUrl(sheet.file_url)}
            target="_blank"
            rel="noopener noreferrer"
            style={{ fontSize: 12, color: ACCENT, fontWeight: 600, textDecoration: 'none' }}
          >
            Open Original ↗
          </a>
        </div>
      </div>

      <div style={{ 
        padding: 16, 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center',
        background: '#fafafa',
        overflow: 'auto',
        maxHeight: '75vh',
        position: 'relative'
      }}>
        {isPdf ? (
          <iframe
            src={getFullUrl(sheet.file_url)}
            style={{ width: '100%', height: '550px', border: 'none', borderRadius: 6 }}
            title={sheet.file_name}
          />
        ) : (
          <div style={{
            transition: 'transform 0.2s ease',
            transform: `scale(${zoom}) rotate(${rotation}deg)`,
            display: 'inline-block',
          }}>
            <a
              href={getFullUrl(sheet.file_url)}
              target="_blank"
              rel="noopener noreferrer"
              title="Click to view full image in a new tab"
              style={{ display: 'block', cursor: 'pointer' }}
            >
              <img
                src={getFullUrl(sheet.file_url)}
                style={{ 
                  maxWidth: '100%', 
                  maxHeight: '70vh', 
                  objectFit: 'contain', 
                  borderRadius: 6,
                  display: 'block',
                  margin: '0 auto'
                }}
                alt={sheet.file_name}
              />
            </a>
          </div>
        )}
      </div>
      {sheet.notes && (
        <div style={{ padding: '12px 16px', borderTop: '1px solid #e2e8f0', fontSize: 12, color: '#475569', background: '#fff' }}>
          <strong>Notes:</strong> {sheet.notes}
        </div>
      )}
    </div>
  );
}


function AnswerSheetsTab() {
  const [timetables,      setTimetables]      = useState([]);
  const [selectedTT,      setSelectedTT]      = useState('');
  const [schedules,       setSchedules]       = useState([]);
  const [selectedSch,     setSelectedSch]     = useState('');
  const [students,        setStudents]        = useState([]);
  const [loading,         setLoading]         = useState(false);
  const [uploading,       setUploading]       = useState(null);   // assignmentId
  const [generating,      setGenerating]      = useState(null);   // assignmentId
  const [bookletAssign,   setBookletAssign]   = useState(null);   // open BookletViewer
  const fileRefs = useRef({});

  // Helper to get the schedule name for the BookletViewer header
  const selectedSchedule = schedules.find(s => String(s.id) === String(selectedSch));

  useEffect(() => {
    coeAPI.getTimetables()
      .then(d => setTimetables(Array.isArray(d) ? d : (d?.results ?? [])))
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!selectedTT) { setSchedules([]); setSelectedSch(''); return; }
    coeAPI.getSchedules({ timetable_id: selectedTT })
      .then(d => setSchedules(Array.isArray(d) ? d : (d?.results ?? [])))
      .catch(() => setSchedules([]));
  }, [selectedTT]);

  const loadStudents = async () => {
    if (!selectedSch) { setStudents([]); return; }
    setLoading(true);
    try {
      const d = await coeAPI.getScheduleStudents(selectedSch);
      setStudents(Array.isArray(d) ? d : (d?.results ?? []));
    } catch { setStudents([]); }
    finally { setLoading(false); }
  };

  useEffect(() => { loadStudents(); }, [selectedSch]); // eslint-disable-line

  // Refresh a single assignment's data from the loaded students list
  const refreshStudents = async (openAssignmentId = null) => {
    if (!selectedSch) return;
    const d = await coeAPI.getScheduleStudents(selectedSch).catch(() => null);
    const list = d ? (Array.isArray(d) ? d : (d?.results ?? [])) : students;
    setStudents(list);
    if (openAssignmentId) {
      const fresh = list.find(x => x.id === openAssignmentId);
      if (fresh) setBookletAssign(fresh);
    }
  };

  // Upload a sheet
  const handleUpload = async (assignmentId, file) => {
    if (!file) return;
    setUploading(assignmentId);
    const fd = new FormData();
    fd.append('file', file);
    try {
      await coeAPI.uploadAnswerSheet(assignmentId, fd);
      await refreshStudents();
    } catch { notify.error('Upload failed.'); }
    finally { setUploading(null); }
  };

  // Delete a single sheet
  const handleDeleteSheet = async (assignmentId, sheetId) => {
    if (!await confirmDialog('Delete this answer sheet?')) return;
    try {
      await coeAPI.deleteAnswerSheet(assignmentId, sheetId);
      await refreshStudents();
    } catch { notify.error('Failed to delete.'); }
  };

  // Generate evaluation file (explicit trigger)
  const handleGenerateEval = async (assignmentId) => {
    setGenerating(assignmentId);
    try {
      await coeAPI.generateEvalFile(assignmentId);
      await refreshStudents();
      notify.success('✅ Evaluation file generated successfully!');
    } catch (err) {
      const msg = err?.response?.data?.error || 'Failed to generate evaluation file.';
      notify.success(`⚠️ ${msg}`);
    } finally {
      setGenerating(null);
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
      <h2 style={{ margin: 0, fontSize: 18, fontWeight: 800, color: '#0f172a' }}>Answer Sheet Management</h2>

      {/* Filters */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 14 }}>
        <Field label="Timetable">
          <select style={inputStyle} value={selectedTT} onChange={e => setSelectedTT(e.target.value)}>
            <option value="">— Choose —</option>
            {timetables.map(t => <option key={t.id} value={t.id}>{t.title}</option>)}
          </select>
        </Field>
        <Field label="Exam Schedule">
          <select style={inputStyle} value={selectedSch} onChange={e => setSelectedSch(e.target.value)} disabled={!selectedTT}>
            <option value="">— Choose —</option>
            {schedules.map(s => <option key={s.id} value={s.id}>{s.paper_label || s.subject} — {fmtDate(s.exam_date)}</option>)}
          </select>
        </Field>
      </div>

      {/* Student rows */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: 40, color: '#64748b' }}>Loading…</div>
      ) : students.length === 0 && selectedSch ? (
        <div style={{ textAlign: 'center', padding: 48, color: '#94a3b8', border: '2px dashed #e2e8f0', borderRadius: 12 }}>
          No students assigned to this exam.
        </div>
      ) : students.length > 0 ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {students.map((a, i) => {
            const hasSheets     = (a.answer_sheets?.length ?? 0) > 0;
            const hasCombined   = !!a.combined_pdf_url;
            const isGenerating  = generating === a.id;
            const isUploading   = uploading === a.id;

            return (
              <div key={a.id} style={{
                background: '#fff',
                border: `1px solid ${hasCombined ? '#bbf7d0' : '#e5e7eb'}`,
                borderRadius: 14,
                padding: '14px 18px',
                display: 'flex', flexDirection: 'column', gap: 10,
                boxShadow: hasCombined ? '0 0 0 1px #86efac22' : 'none',
                transition: 'border-color 0.2s',
              }}>
                {/* Row header */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 10 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <div style={{ width: 32, height: 32, borderRadius: 8, background: ACCENT_L, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 800, color: ACCENT, flexShrink: 0 }}>
                      {String(i + 1).padStart(2, '0')}
                    </div>
                    <div>
                      <div style={{ fontWeight: 700, fontSize: 14, color: '#0f172a' }}>{a.student_name}</div>
                      <div style={{ fontSize: 11, color: '#64748b' }}>{a.student_id_num || '—'}</div>
                    </div>
                  </div>

                  {/* Action buttons */}
                  <div style={{ display: 'flex', gap: 7, alignItems: 'center', flexWrap: 'wrap' }}>
                    {/* Attendance badge */}
                    <span style={{ fontSize: 10, padding: '2px 8px', borderRadius: 99, fontWeight: 700, background: a.attendance === 'PRESENT' ? '#dcfce7' : a.attendance === 'ABSENT' ? '#fee2e2' : '#f3f4f6', color: a.attendance === 'PRESENT' ? '#166534' : a.attendance === 'ABSENT' ? '#dc2626' : '#64748b' }}>
                      {a.attendance_display}
                    </span>

                    {/* Sheet count */}
                    <span style={{ fontSize: 11, color: '#64748b' }}>
                      {a.answer_sheets?.length || 0} sheet(s)
                    </span>

                    {/* Combined PDF status badge */}
                    {hasCombined ? (
                      <span style={{ fontSize: 10, padding: '2px 8px', borderRadius: 99, fontWeight: 700, background: '#dcfce7', color: '#166534', display: 'flex', alignItems: 'center', gap: 3 }}>
                        ✅ Eval Doc Ready
                      </span>
                    ) : hasSheets ? (
                      <span style={{ fontSize: 10, padding: '2px 8px', borderRadius: 99, fontWeight: 700, background: '#fef9c3', color: '#92400e' }}>
                        ⏳ Not Combined
                      </span>
                    ) : null}

                    {/* Preview button */}
                    {hasSheets && (
                      <button
                        onClick={() => setBookletAssign(a)}
                        style={{ padding: '5px 12px', borderRadius: 8, border: 'none', color: '#fff', fontWeight: 700, fontSize: 11, cursor: 'pointer', background: 'linear-gradient(135deg, #3b82f6, #6366f1)' }}
                      >
                        👁 Preview
                      </button>
                    )}

                    {/* Generate Evaluation File button */}
                    {hasSheets && (
                      <button
                        onClick={() => handleGenerateEval(a.id)}
                        disabled={isGenerating}
                        style={{
                          padding: '5px 12px', borderRadius: 8,
                          border: 'none', fontWeight: 700, fontSize: 11,
                          cursor: isGenerating ? 'not-allowed' : 'pointer',
                          background: isGenerating ? '#94a3b8' : 'linear-gradient(135deg, #f59e0b, #ef4444)',
                          color: '#fff',
                          display: 'flex', alignItems: 'center', gap: 4,
                          whiteSpace: 'nowrap',
                        }}
                        title={hasCombined ? 'Re-generate evaluation file' : 'Combine all sheets into evaluation document'}
                      >
                        {isGenerating ? '⏳ Generating…' : hasCombined ? '🔄 Re-generate' : '⚡ Generate Eval File'}
                      </button>
                    )}

                    {/* Upload sheet */}
                    <label style={{ cursor: 'pointer' }}>
                      <input
                        type="file"
                        accept=".pdf,.jpg,.jpeg,.png,.bmp,.tiff,.webp"
                        style={{ display: 'none' }}
                        ref={el => { fileRefs.current[a.id] = el; }}
                        onChange={e => handleUpload(a.id, e.target.files[0])}
                      />
                      <span style={{ padding: '5px 12px', borderRadius: 8, border: `1px solid ${ACCENT}`, color: ACCENT, fontWeight: 700, fontSize: 11, cursor: 'pointer', background: '#fff', whiteSpace: 'nowrap' }}>
                        {isUploading ? '⏳ Uploading…' : '+ Upload Sheet'}
                      </span>
                    </label>
                  </div>
                </div>

                {/* Sheet file chips */}
                {hasSheets && (
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    {a.answer_sheets.map(sh => (
                      <div key={sh.id} style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, padding: '6px 10px', display: 'flex', alignItems: 'center', gap: 6 }}>
                        <span style={{ fontSize: 16 }}>📄</span>
                        <a href={getFullUrl(sh.file_url)} target="_blank" rel="noopener noreferrer" style={{ fontSize: 11, color: ACCENT, fontWeight: 600, textDecoration: 'none', maxWidth: 140, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {sh.file_name || `Sheet ${sh.id}`}
                        </a>
                        <button
                          onClick={() => handleDeleteSheet(a.id, sh.id)}
                          style={{ background: 'none', border: 'none', color: '#dc2626', cursor: 'pointer', fontWeight: 700, fontSize: 13, padding: '0 2px' }}
                        >✕</button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      ) : (
        <div style={{ textAlign: 'center', padding: 48, color: '#94a3b8', border: '2px dashed #e2e8f0', borderRadius: 12 }}>
          Select a timetable and exam schedule to manage answer sheets.
        </div>
      )}

      {/* ── BookletViewer full-screen preview ─────────────────────────── */}
      {bookletAssign && (
        <BookletViewer
          pdfUrl={bookletAssign.combined_pdf_url
            ? getFullUrl(bookletAssign.combined_pdf_url)
            : null}
          studentName={bookletAssign.student_name}
          studentId={bookletAssign.student_id_num}
          subject={selectedSchedule?.paper_label || selectedSchedule?.subject || ''}
          fallbackSheets={bookletAssign.answer_sheets || []}
          onClose={() => setBookletAssign(null)}
        />
      )}
    </div>
  );
}



// ══════════════════════════════════════════════════════════════════════════════
// TAB: Question Papers
// ══════════════════════════════════════════════════════════════════════════════
function QuestionPapersTab() {
  const [papers,      setPapers]      = useState([]);
  const [schedules,   setSchedules]   = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [showForm,    setShowForm]    = useState(false);
  const [saving,      setSaving]      = useState(false);
  const [activePaper, setActivePaper] = useState(null);
  const [paperForm,   setPaperForm]   = useState({ title: '', instructions: '', duration_minutes: 180, schedule: '' });

  // Section form
  const [showSectionForm, setShowSectionForm] = useState(false);
  const [sectionForm, setSectionForm] = useState({ title: '', instructions: '', marks_per_q: 5, total_questions: 5, attempt_questions: 0, order: 1 });
  const [savingSection, setSavingSection] = useState(false);
  const [sectionWarning, setSectionWarning] = useState('');

  // Question form
  const [activeSection, setActiveSection] = useState(null);
  const [showQForm,     setShowQForm]     = useState(false);
  const [qForm,         setQForm]         = useState({ question_type: 'SHORT', text: '', marks: 5, order: 1, options: '', correct_answer: '' });
  const [savingQ,       setSavingQ]       = useState(false);

  const load = () => {
    setLoading(true);
    Promise.all([
      coeAPI.getQuestionPapers(),
      coeAPI.getSchedules()
    ])
      .then(([pData, sData]) => {
        setPapers(Array.isArray(pData) ? pData : (pData.results || []));
        setSchedules(Array.isArray(sData) ? sData : (sData.results || []));
      })
      .catch(() => {
        setPapers([]);
        setSchedules([]);
      })
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  const handleCreatePaper = async () => {
    if (!paperForm.title.trim() || !paperForm.schedule) return;
    setSaving(true);
    try {
      await coeAPI.createQuestionPaper(paperForm);
      setShowForm(false);
      setPaperForm({ title: '', instructions: '', duration_minutes: 180, schedule: '' });
      load();
    } catch { notify.error('Failed to create question paper.'); }
    finally { setSaving(false); }
  };

  const handleAddSection = async () => {
    if (!activePaper || !sectionForm.title.trim()) return;
    setSavingSection(true);
    try {
      await coeAPI.addSection(activePaper.id, sectionForm);
      const d = await coeAPI.getQuestionPapers();
      const refreshed = (Array.isArray(d) ? d : (d.results || [])).find(p => p.id === activePaper.id);
      if (refreshed) setActivePaper(refreshed);
      setPapers(Array.isArray(d) ? d : (d.results || []));
      setShowSectionForm(false);
    } catch { notify.error('Failed.'); }
    finally { setSavingSection(false); }
  };

  const handleAddQuestion = async () => {
    if (!activeSection || !qForm.text.trim()) return;
    setSavingQ(true);
    try {
      const payload = {
        ...qForm,
        options: qForm.question_type === 'MCQ' ? qForm.options.split('\n').map(s => s.trim()).filter(Boolean) : null,
      };
      await coeAPI.addQuestion(activeSection, payload);
      const d = await coeAPI.getQuestionPapers();
      const refreshed = (Array.isArray(d) ? d : (d.results || [])).find(p => p.id === activePaper?.id);
      if (refreshed) setActivePaper(refreshed);
      setPapers(Array.isArray(d) ? d : (d.results || []));
      setShowQForm(false);
      setQForm({ question_type: 'SHORT', text: '', marks: 5, order: 1, options: '', correct_answer: '' });
    } catch { notify.error('Failed.'); }
    finally { setSavingQ(false); }
  };

  const calcTotalMarks = (paper) => {
    if (!paper?.sections) return 0;
    return paper.sections.reduce((sum, sec) => {
      const q = sec.attempt_questions || sec.total_questions || 0;
      return sum + (parseInt(sec.marks_per_q || 0)) * q;
    }, 0);
  };

  // Section total preview
  const sectionTotal = (parseInt(sectionForm.marks_per_q) || 0) * (parseInt(sectionForm.total_questions) || 0);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 10 }}>
        <h2 style={{ margin: 0, fontSize: 18, fontWeight: 800, color: '#0f172a' }}>Question Papers</h2>
        <Btn onClick={() => setShowForm(p => !p)}>{showForm ? '✕ Cancel' : '+ New Paper'}</Btn>
      </div>

      {showForm && (
        <div style={{ background: '#fff', border: `1px solid ${ACCENT_B}`, borderRadius: 14, padding: 20, display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div style={{ fontWeight: 700, fontSize: 14, color: '#0f172a' }}>New Question Paper</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 14 }}>
            <Field label="Link Scheduled Subject *">
              <select style={inputStyle} value={paperForm.schedule} onChange={e => {
                const sId = e.target.value;
                const found = schedules.find(s => String(s.id) === String(sId));
                setPaperForm(p => ({
                  ...p,
                  schedule: sId,
                  title: found ? `${found.subject} (${found.exam_type_display}) - Paper` : p.title
                }));
              }}>
                <option value="">— Select Schedule —</option>
                {schedules.map(s => (
                  <option key={s.id} value={s.id}>{s.subject} ({s.exam_type_display}) — {fmtDate(s.exam_date)}</option>
                ))}
              </select>
            </Field>
            <Field label="Paper Title *">
              <input style={inputStyle} value={paperForm.title} onChange={e => setPaperForm(p => ({...p, title: e.target.value}))} placeholder="e.g. MBBS 1st Year — Anatomy Nov 2025" />
            </Field>
            <Field label="Duration (mins)">
              <input style={inputStyle} type="number" value={paperForm.duration_minutes} onChange={e => setPaperForm(p => ({...p, duration_minutes: parseInt(e.target.value)||180}))} />
            </Field>
          </div>
          <Field label="General Instructions">
            <textarea style={{ ...inputStyle, minHeight: 60, resize: 'vertical' }} value={paperForm.instructions} onChange={e => setPaperForm(p => ({...p, instructions: e.target.value}))} placeholder="Instructions for the entire paper…" />
          </Field>
          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
            <Btn ghost onClick={() => setShowForm(false)}>Cancel</Btn>
            <Btn onClick={handleCreatePaper} disabled={saving || !paperForm.title.trim() || !paperForm.schedule}>{saving ? 'Creating…' : '💾 Create'}</Btn>
          </div>
        </div>
      )}

      {loading ? (
        <div style={{ textAlign: 'center', padding: 40, color: '#64748b' }}>Loading…</div>
      ) : papers.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 48, color: '#94a3b8', border: '2px dashed #e2e8f0', borderRadius: 12 }}>No question papers yet.</div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: activePaper ? '1fr 2fr' : '1fr', gap: 18 }}>
          {/* Paper list */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {papers.map(p => (
              <div key={p.id}
                onClick={() => setActivePaper(activePaper?.id === p.id ? null : p)}
                style={{ background: '#fff', border: `1px solid ${activePaper?.id === p.id ? ACCENT : '#e5e7eb'}`, borderRadius: 12, padding: '14px 18px', cursor: 'pointer', transition: 'border-color .15s' }}>
                <div style={{ fontWeight: 700, fontSize: 14, color: '#0f172a' }}>{p.title}</div>
                <div style={{ fontSize: 12, color: '#64748b', marginTop: 3 }}>
                  {p.sections?.length || 0} section(s) · {calcTotalMarks(p)} marks · {p.duration_minutes} min
                </div>
                <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                  <span style={{ fontSize: 10, padding: '2px 8px', borderRadius: 99, fontWeight: 700, background: p.is_finalized ? '#dcfce7' : '#fef3c7', color: p.is_finalized ? '#166534' : '#92400e' }}>
                    {p.is_finalized ? '✓ Finalized' : 'Draft'}
                  </span>
                </div>
              </div>
            ))}
          </div>

          {/* Paper builder */}
          {activePaper && (
            <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 14, overflow: 'hidden' }}>
              <div style={{ background: ACCENT, padding: '14px 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <div style={{ fontWeight: 800, fontSize: 15, color: '#fff' }}>{activePaper.title}</div>
                  <div style={{ fontSize: 12, color: 'rgba(255,255,255,.8)' }}>Total: {calcTotalMarks(activePaper)} marks · {activePaper.duration_minutes} min</div>
                </div>
                <button onClick={() => { setShowSectionForm(p => !p); }} style={{ background: 'rgba(255,255,255,.2)', border: 'none', color: '#fff', padding: '6px 14px', borderRadius: 8, cursor: 'pointer', fontWeight: 700, fontSize: 12, fontFamily: 'inherit' }}>
                  + Add Section
                </button>
              </div>

              {showSectionForm && (
                <div style={{ background: '#f8fafc', padding: '14px 18px', borderBottom: '1px solid #e5e7eb' }}>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: 12, marginBottom: 12 }}>
                    <Field label="Section Title *">
                      <input style={inputStyle} value={sectionForm.title} onChange={e => setSectionForm(p => ({...p, title: e.target.value}))} placeholder="Section A" />
                    </Field>
                    <Field label="Marks / Question">
                      <input style={inputStyle} type="number" step="1" min="1" value={sectionForm.marks_per_q}
                        onChange={e => { setSectionWarning(''); setSectionForm(p => ({...p, marks_per_q: parseInt(e.target.value)||1})); }} />
                    </Field>
                    <Field label="Total Questions">
                      <input style={inputStyle} type="number" step="1" min="1" value={sectionForm.total_questions}
                        onChange={e => { setSectionWarning(''); setSectionForm(p => ({...p, total_questions: parseInt(e.target.value)||1})); }} />
                    </Field>
                    <Field label="Section Total (auto)">
                      <div style={{ padding: '8px 10px', background: ACCENT_L, border: `1px solid ${ACCENT_B}`, borderRadius: 8, fontSize: 15, fontWeight: 800, color: ACCENT }}>
                        {sectionTotal} marks
                      </div>
                    </Field>
                    <Field label="Attempt (0=all)">
                      <input style={inputStyle} type="number" value={sectionForm.attempt_questions} onChange={e => setSectionForm(p => ({...p, attempt_questions: parseInt(e.target.value)||0}))} />
                    </Field>
                    <Field label="Order">
                      <input style={inputStyle} type="number" value={sectionForm.order} onChange={e => setSectionForm(p => ({...p, order: parseInt(e.target.value)||1}))} />
                    </Field>
                  </div>
                  {sectionWarning && <div style={{ color: '#dc2626', fontSize: 12, marginBottom: 8 }}>⚠ {sectionWarning}</div>}
                  <Field label="Section Instructions">
                    <textarea style={{ ...inputStyle, minHeight: 50, resize: 'vertical' }} value={sectionForm.instructions} onChange={e => setSectionForm(p => ({...p, instructions: e.target.value}))} />
                  </Field>
                  <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 10 }}>
                    <Btn small ghost onClick={() => setShowSectionForm(false)}>Cancel</Btn>
                    <Btn small onClick={handleAddSection} disabled={savingSection}>{savingSection ? 'Adding…' : 'Add Section'}</Btn>
                  </div>
                </div>
              )}

              <div style={{ padding: '14px 18px', display: 'flex', flexDirection: 'column', gap: 16, maxHeight: 600, overflowY: 'auto' }}>
                {(!activePaper.sections || activePaper.sections.length === 0) && (
                  <div style={{ textAlign: 'center', padding: 32, color: '#94a3b8', fontSize: 13 }}>No sections yet. Add a section above.</div>
                )}
                {activePaper.sections?.map(sec => (
                  <div key={sec.id} style={{ border: '1px solid #e2e8f0', borderRadius: 10, overflow: 'hidden' }}>
                    <div style={{ background: '#f8fafc', padding: '10px 14px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <div>
                        <span style={{ fontWeight: 700, fontSize: 13, color: '#0f172a' }}>{sec.title}</span>
                        <span style={{ fontSize: 11, color: '#64748b', marginLeft: 10 }}>
                          {sec.marks_per_q} marks each · {sec.attempt_questions > 0 ? `Attempt any ${sec.attempt_questions}` : `All ${sec.total_questions}`}
                        </span>
                        <span style={{ fontSize: 11, fontWeight: 700, color: ACCENT, marginLeft: 10, background: ACCENT_L, padding: '1px 8px', borderRadius: 99 }}>
                          Total: {(parseInt(sec.marks_per_q)||0) * (sec.attempt_questions > 0 ? sec.attempt_questions : (sec.total_questions || 0))} marks
                        </span>
                      </div>
                      <button onClick={() => { setActiveSection(sec.id); setShowQForm(p => p && activeSection === sec.id ? false : true); }}
                        style={{ background: 'none', border: `1px solid ${ACCENT}`, color: ACCENT, padding: '4px 10px', borderRadius: 6, cursor: 'pointer', fontSize: 11, fontWeight: 700, fontFamily: 'inherit' }}>
                        + Question
                      </button>
                    </div>
                    {sec.instructions && (
                      <div style={{ background: '#fffbeb', padding: '6px 14px', fontSize: 11, color: '#92400e', borderBottom: '1px solid #fde68a' }}>
                        {sec.instructions}
                      </div>
                    )}
                    {showQForm && activeSection === sec.id && (
                      <div style={{ background: '#f8fafc', padding: '12px 14px', borderBottom: '1px solid #e2e8f0', display: 'flex', flexDirection: 'column', gap: 10 }}>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: 10 }}>
                          <Field label="Type">
                            <select style={inputStyle} value={qForm.question_type} onChange={e => setQForm(p => ({...p, question_type: e.target.value}))}>
                              {Q_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                            </select>
                          </Field>
                          <Field label="Order">
                            <input style={inputStyle} type="number" value={qForm.order} onChange={e => setQForm(p => ({...p, order: parseInt(e.target.value)||1}))} />
                          </Field>
                          <Field label="Marks (from section)">
                            <div style={{ padding: '8px 10px', background: ACCENT_L, border: `1px solid ${ACCENT_B}`, borderRadius: 8, fontSize: 13, fontWeight: 700, color: ACCENT }}>
                              {sec.marks_per_q} marks each
                            </div>
                          </Field>
                        </div>
                        <Field label="Question Text *">
                          <textarea style={{ ...inputStyle, minHeight: 60, resize: 'vertical' }} value={qForm.text} onChange={e => setQForm(p => ({...p, text: e.target.value}))} placeholder="Enter the question…" />
                        </Field>
                        {qForm.question_type === 'MCQ' && (
                          <Field label="Options (one per line)">
                            <textarea style={{ ...inputStyle, minHeight: 60, resize: 'vertical' }} value={qForm.options} onChange={e => setQForm(p => ({...p, options: e.target.value}))} placeholder={"A. Option 1\nB. Option 2\nC. Option 3\nD. Option 4"} />
                          </Field>
                        )}
                        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                          <Btn small ghost onClick={() => setShowQForm(false)}>Cancel</Btn>
                          <Btn small onClick={handleAddQuestion} disabled={savingQ || !qForm.text.trim()}>{savingQ ? 'Adding…' : '+ Add Question'}</Btn>
                        </div>
                      </div>
                    )}
                    <div>
                      {sec.questions?.length === 0 && (
                        <div style={{ padding: '12px 14px', fontSize: 12, color: '#94a3b8' }}>No questions added yet.</div>
                      )}
                      {sec.questions?.map((q, qi) => (
                        <div key={q.id} style={{ padding: '10px 14px', borderBottom: '1px solid #f1f5f9', display: 'flex', gap: 10 }}>
                          <div style={{ fontWeight: 700, fontSize: 13, color: ACCENT, minWidth: 28 }}>{qi + 1}.</div>
                          <div style={{ flex: 1 }}>
                            <div style={{ fontSize: 13, color: '#0f172a' }}>{q.text}</div>
                            {q.options && Array.isArray(q.options) && (
                              <div style={{ marginTop: 4, display: 'flex', flexDirection: 'column', gap: 2 }}>
                                {q.options.map((o, oi) => (
                                  <div key={oi} style={{ fontSize: 11, color: '#64748b', marginLeft: 10 }}>{o}</div>
                                ))}
                              </div>
                            )}
                          </div>
                          <div style={{ fontSize: 10, padding: '2px 6px', borderRadius: 99, background: '#f3f4f6', color: '#64748b', whiteSpace: 'nowrap' }}>{q.question_type}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>

              {/* Print preview hint */}
              <div style={{ padding: '12px 18px', borderTop: '1px solid #f1f5f9', display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
                <Btn ghost small onClick={() => window.open(`/coe/question-papers/${activePaper.id}/print`, '_blank')}>
                  🖨 Print Preview
                </Btn>
                <Btn small onClick={async () => {
                  try { await coeAPI.updateQuestionPaper(activePaper.id, { is_finalized: !activePaper.is_finalized }); load(); setActivePaper(null); }
                  catch { notify.error('Failed.'); }
                }}>
                  {activePaper.is_finalized ? 'Unfinalize' : '✓ Finalize Paper'}
                </Btn>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// TAB: Thesis Evaluation
// ══════════════════════════════════════════════════════════════════════════════
function ThesisEvaluationTab() {
  const [submissions, setSubmissions] = useState([]);
  const [proposals,   setProposals]   = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [search,      setSearch]      = useState('');
  const [subTab,      setSubTab]      = useState('PG');
  const [thesisMode,  setThesisMode]  = useState('SUBMISSIONS'); // 'SUBMISSIONS' or 'PROPOSALS'
  const [selected,    setSelected]    = useState(null);
  const [selectedProposal, setSelectedProposal] = useState(null);
  const [marksInput,  setMarksInput]  = useState('');
  const [remarksInput,setRemarksInput]= useState('');
  const [saving,      setSaving]      = useState(false);
  const [previewDoc,  setPreviewDoc]  = useState(null);
  const [zoom,        setZoom]        = useState(1);

  const loadSubmissions = () => {
    setLoading(true);
    coeAPI.getThesis()
      .then(d => setSubmissions(Array.isArray(d) ? d : []))
      .catch(() => setSubmissions([]))
      .finally(() => setLoading(false));
  };

  const loadProposals = () => {
    setLoading(true);
    coeEthicsAPI.getThesisProposals()
      .then(d => setProposals(Array.isArray(d) ? d : []))
      .catch(() => setProposals([]))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (thesisMode === 'SUBMISSIONS') {
      loadSubmissions();
    } else {
      loadProposals();
    }
  }, [thesisMode]);

  const filteredSubmissions = submissions.filter(s => {
    if (s.student_course !== subTab) return false;
    if (!search) return true;
    const q = search.toLowerCase();
    return (s.student_name || '').toLowerCase().includes(q) ||
      (s.student_id_num || '').toLowerCase().includes(q) ||
      (s.student_email || '').toLowerCase().includes(q) ||
      (s.title || '').toLowerCase().includes(q);
  });

  const filteredProposals = proposals.filter(p => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (p.student_name || '').toLowerCase().includes(q) ||
      (p.student_email || '').toLowerCase().includes(q) ||
      (p.title || '').toLowerCase().includes(q) ||
      (p.department_name || '').toLowerCase().includes(q);
  });

  const openSubmission = (s) => {
    setSelected(s);
    setMarksInput(s.marks != null ? String(s.marks) : '');
    setRemarksInput(s.remarks || '');
  };

  const handleEvaluate = async (approve = false) => {
    if (!selected) return;
    const m = marksInput.trim();
    if (m !== '' && (!/^\d+$/.test(m))) { notify.success('Marks must be a whole number.'); return; }
    if (approve && m === '') { notify.success('Enter marks before approving.'); return; }
    setSaving(true);
    try {
      const updated = await coeAPI.evaluateThesis(selected.id, {
        marks: m !== '' ? parseInt(m) : null,
        remarks: remarksInput,
        approve,
      });
      setSelected(updated);
      setSubmissions(p => p.map(s => s.id === updated.id ? updated : s));
    } catch (e) {
      notify.error(e?.response?.data?.error || 'Failed to save.');
    } finally { setSaving(false); }
  };

  const getFileIcon = (url) => {
    if (!url) return '📎';
    if (url.endsWith('.pdf')) return '📕';
    if (/\.(jpg|jpeg|png|gif|webp)/i.test(url)) return '🖼';
    return '📄';
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
      
      {/* Top Toggle Mode */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 10 }}>
        <div style={{ display: 'flex', gap: 12 }}>
          <button 
            onClick={() => { setThesisMode('SUBMISSIONS'); setSelected(null); setSelectedProposal(null); }}
            style={{
              padding: '8px 16px',
              borderRadius: '8px',
              border: 'none',
              background: thesisMode === 'SUBMISSIONS' ? ACCENT : '#e2e8f0',
              color: thesisMode === 'SUBMISSIONS' ? '#fff' : '#475569',
              fontWeight: 700,
              fontSize: '13px',
              cursor: 'pointer',
              fontFamily: 'inherit',
            }}
          >
            🎓 Thesis Submissions
          </button>
          <button 
            onClick={() => { setThesisMode('PROPOSALS'); setSelected(null); setSelectedProposal(null); }}
            style={{
              padding: '8px 16px',
              borderRadius: '8px',
              border: 'none',
              background: thesisMode === 'PROPOSALS' ? ACCENT : '#e2e8f0',
              color: thesisMode === 'PROPOSALS' ? '#fff' : '#475569',
              fontWeight: 700,
              fontSize: '13px',
              cursor: 'pointer',
              fontFamily: 'inherit',
            }}
          >
            📋 Thesis Proposals
          </button>
        </div>
        <div style={{ fontSize: 12, color: '#64748b' }}>
          {thesisMode === 'SUBMISSIONS' 
            ? `${submissions.filter(s => s.student_course === subTab).length} Submissions`
            : `${proposals.length} Proposals`
          }
        </div>
      </div>

      {thesisMode === 'SUBMISSIONS' ? (
        <>
          {/* Sub Tabs */}
          <div style={{ display: 'flex', gap: 10, borderBottom: '1px solid #e2e8f0', paddingBottom: 6 }}>
            <button onClick={() => { setSubTab('PG'); setSelected(null); }}
              style={{
                padding: '8px 16px',
                border: 'none',
                borderBottom: subTab === 'PG' ? `2px solid ${ACCENT}` : '2px solid transparent',
                background: 'none',
                color: subTab === 'PG' ? ACCENT : '#64748b',
                fontWeight: subTab === 'PG' ? 700 : 500,
                fontSize: 13,
                cursor: 'pointer',
                fontFamily: 'inherit',
                transition: 'all .15s'
              }}>
              Post Graduate (PG)
            </button>
            <button onClick={() => { setSubTab('SUPER_SPECIALITY'); setSelected(null); }}
              style={{
                padding: '8px 16px',
                border: 'none',
                borderBottom: subTab === 'SUPER_SPECIALITY' ? `2px solid ${ACCENT}` : '2px solid transparent',
                background: 'none',
                color: subTab === 'SUPER_SPECIALITY' ? ACCENT : '#64748b',
                fontWeight: subTab === 'SUPER_SPECIALITY' ? 700 : 500,
                fontSize: 13,
                cursor: 'pointer',
                fontFamily: 'inherit',
                transition: 'all .15s'
              }}>
              Super Speciality
            </button>
          </div>

          {/* Search */}
          <input style={{ ...inputStyle, maxWidth: 340 }} placeholder="🔍 Search submissions…"
            value={search} onChange={e => setSearch(e.target.value)} />

          {loading ? (
            <div style={{ textAlign: 'center', padding: 48, color: '#64748b' }}>Loading…</div>
          ) : filteredSubmissions.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 48, color: '#94a3b8', border: '2px dashed #e2e8f0', borderRadius: 12 }}>
              {search ? 'No submissions match your search.' : 'No thesis submissions yet.'}
            </div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: selected ? '1fr 1.5fr' : '1fr', gap: 18 }}>
              {/* List */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {filteredSubmissions.map(s => (
                  <div key={s.id} onClick={() => openSubmission(s)}
                    style={{ background: '#fff', border: `1px solid ${selected?.id === s.id ? ACCENT : '#e5e7eb'}`, borderRadius: 12, padding: '14px 18px', cursor: 'pointer', transition: 'border-color .15s' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 10 }}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontWeight: 700, fontSize: 14, color: '#0f172a', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{s.title}</div>
                        <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>
                          {s.student_name} (Reg No: {s.student_id_num || 'N/A'}) · {s.student_course_display || s.student_course}
                        </div>
                        <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 2 }}>{s.documents?.length || 0} document(s) uploaded</div>
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 4 }}>
                        <span style={{ padding: '2px 8px', borderRadius: 99, fontSize: 10, fontWeight: 700,
                          background: s.marks_approved ? '#dcfce7' : s.marks != null ? '#fef3c7' : '#f3f4f6',
                          color: s.marks_approved ? '#166534' : s.marks != null ? '#92400e' : '#64748b' }}>
                          {s.marks_approved ? '✓ Approved' : s.marks != null ? `${s.marks} marks (Draft)` : 'Pending'}
                        </span>
                        {s.student_course !== 'MBBS' && s.status && (
                          <span style={{ padding: '2px 8px', borderRadius: 99, fontSize: 9, fontWeight: 700,
                            background: s.status === 'READY_FOR_COE_EVALUATION' ? '#dbeafe' : s.status === 'FINAL_SUBMITTED' ? '#fef3c7' : '#e0e7ff',
                            color: s.status === 'READY_FOR_COE_EVALUATION' ? '#1d4ed8' : s.status === 'FINAL_SUBMITTED' ? '#d97706' : '#4338ca' }}>
                            {s.status.replace(/_/g, ' ')}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Detail panel */}
              {selected && (
                <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 14, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
                  {/* Header */}
                  <div style={{ background: ACCENT, padding: '14px 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <div>
                      <div style={{ fontWeight: 800, fontSize: 15, color: '#fff' }}>{selected.title}</div>
                      <div style={{ fontSize: 12, color: 'rgba(255,255,255,.85)', marginTop: 2 }}>
                        Student: {selected.student_name} (Reg No: {selected.student_id_num || 'N/A'}) · {selected.student_course_display || selected.student_course}
                        {selected.student_department && ` · ${selected.student_department}`}
                      </div>
                    </div>
                    <button onClick={() => setSelected(null)} style={{ background: 'rgba(255,255,255,.2)', border: 'none', color: '#fff', borderRadius: 8, padding: '4px 10px', cursor: 'pointer', fontSize: 16, fontFamily: 'inherit' }}>✕</button>
                  </div>

                  <div style={{ padding: '16px 20px', display: 'flex', flexDirection: 'column', gap: 16, overflowY: 'auto', flex: 1 }}>
                    {/* Description */}
                    {selected.description && (
                      <div style={{ background: '#f8fafc', borderRadius: 10, padding: '10px 14px', fontSize: 13, color: '#374151' }}>{selected.description}</div>
                    )}

                    {/* Documents */}
                    <div>
                      <div style={{ fontWeight: 700, fontSize: 13, color: '#0f172a', marginBottom: 10 }}>📎 Submitted Documents ({selected.documents?.length || 0})</div>
                      {(!selected.documents || selected.documents.length === 0) ? (
                        <div style={{ color: '#94a3b8', fontSize: 13 }}>No documents uploaded.</div>
                      ) : (
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                          {selected.documents.map(doc => (
                            <div key={doc.id} style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, padding: '8px 12px', display: 'flex', alignItems: 'center', gap: 8 }}>
                              <span style={{ fontSize: 20 }}>{getFileIcon(doc.file_url)}</span>
                              <div>
                                <div style={{ fontSize: 12, fontWeight: 600, color: '#0f172a' }}>{doc.file_name || `Document ${doc.id}`}</div>
                                <div style={{ display: 'flex', gap: 8, marginTop: 2 }}>
                                  <a href={getFullUrl(doc.file_url)} target="_blank" rel="noopener noreferrer"
                                    style={{ fontSize: 11, color: ACCENT, fontWeight: 600, textDecoration: 'none' }}>Open ↗</a>
                                  <button onClick={() => { setPreviewDoc(doc); setZoom(1); }}
                                    style={{ background: 'none', border: 'none', fontSize: 11, color: ACCENT, fontWeight: 600, cursor: 'pointer', padding: 0, fontFamily: 'inherit' }}>Preview</button>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* Evaluation */}
                    <div style={{ background: selected.marks_approved ? '#f0fdf4' : '#f8fafc', border: `1px solid ${selected.marks_approved ? '#86efac' : ACCENT_B}`, borderRadius: 12, padding: '14px 18px' }}>
                      <div style={{ fontWeight: 700, fontSize: 13, color: '#0f172a', marginBottom: 10 }}>
                        📊 Evaluation {selected.marks_approved && <span style={{ color: '#16a34a', marginLeft: 8 }}>✓ Marks Approved</span>}
                      </div>

                      {selected.marks_approved ? (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                          <div style={{ fontSize: 24, fontWeight: 800, color: '#16a34a' }}>{selected.marks} marks</div>
                          {selected.remarks && <div style={{ fontSize: 13, color: '#374151' }}>{selected.remarks}</div>}
                          <div style={{ fontSize: 11, color: '#64748b' }}>
                            Evaluated by {selected.evaluated_by_name || '—'} · {selected.evaluated_at ? fmtDate(selected.evaluated_at) : ''}
                          </div>
                        </div>
                      ) : (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                          <Field label="Marks (integer)">
                            <input style={inputStyle} type="number" step="1" min="0" placeholder="e.g. 75"
                              value={marksInput} onChange={e => setMarksInput(e.target.value.replace(/[^0-9]/g, ''))} />
                          </Field>
                          <Field label="Remarks / Feedback">
                            <textarea style={{ ...inputStyle, minHeight: 70, resize: 'vertical' }}
                              value={remarksInput} onChange={e => setRemarksInput(e.target.value)}
                              placeholder="Optional comments for the student…" />
                          </Field>
                          <div style={{ display: 'flex', gap: 10 }}>
                            <Btn ghost small onClick={() => handleEvaluate(false)} disabled={saving}>
                              {saving ? 'Saving…' : '💾 Save Draft'}
                            </Btn>
                            <Btn small onClick={() => handleEvaluate(true)} disabled={saving || !marksInput}>
                              {saving ? 'Approving…' : '✓ Approve Marks'}
                            </Btn>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </>
      ) : (
        <>
          {/* Proposals List */}
          <input style={{ ...inputStyle, maxWidth: 340 }} placeholder="🔍 Search proposals…"
            value={search} onChange={e => setSearch(e.target.value)} />

          {loading ? (
            <div style={{ textAlign: 'center', padding: 48, color: '#64748b' }}>Loading…</div>
          ) : filteredProposals.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 48, color: '#94a3b8', border: '2px dashed #e2e8f0', borderRadius: 12 }}>
              {search ? 'No proposals match your search.' : 'No thesis proposals submitted.'}
            </div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: selectedProposal ? '1fr 1.2fr' : '1fr', gap: 18 }}>
              {/* Proposals List */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {filteredProposals.map(p => (
                  <div key={p.id} onClick={() => setSelectedProposal(p)}
                    style={{ background: '#fff', border: `1px solid ${selectedProposal?.id === p.id ? ACCENT : '#e5e7eb'}`, borderRadius: 12, padding: '14px 18px', cursor: 'pointer', transition: 'border-color .15s' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 10 }}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontWeight: 700, fontSize: 14, color: '#0f172a', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.title}</div>
                        <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>
                          {p.student_name} (Reg No: {p.student_id_num || 'N/A'}) · Dept: {p.department_name}
                        </div>
                      </div>
                      <span className={`badge ${p.status === 'APPROVED' ? 'badge-green' : p.status === 'REJECTED' ? 'badge-red' : 'badge-orange'}`} style={{ fontSize: '10px' }}>
                        {p.status}
                      </span>
                    </div>
                  </div>
                ))}
              </div>

              {/* Proposal Detail Panel */}
              {selectedProposal && (
                <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 14, display: 'flex', flexDirection: 'column' }}>
                  {/* Header */}
                  <div style={{ background: ACCENT, padding: '14px 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <div>
                      <div style={{ fontWeight: 800, fontSize: 14, color: '#fff' }}>{selectedProposal.title}</div>
                      <div style={{ fontSize: 12, color: 'rgba(255,255,255,.85)', marginTop: 2 }}>
                        Student: {selectedProposal.student_name} (Reg No: {selectedProposal.student_id_num || 'N/A'})
                      </div>
                    </div>
                    <button onClick={() => setSelectedProposal(null)} style={{ background: 'none', border: 'none', color: '#fff', fontSize: '20px', cursor: 'pointer' }}>✕</button>
                  </div>

                  <div style={{ padding: '16px 20px', display: 'flex', flexDirection: 'column', gap: 14, overflowY: 'auto' }}>
                    <div>
                      <span style={labelStyle}>Abstract / Summary</span>
                      <div style={{ fontSize: 12, color: '#334155', background: '#f8fafc', padding: '10px', borderRadius: 8, whiteSpace: 'pre-wrap' }}>
                        {selectedProposal.abstract}
                      </div>
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                      <div>
                        <span style={labelStyle}>Department</span>
                        <div style={{ fontSize: 12, fontWeight: 600 }}>{selectedProposal.department_name}</div>
                      </div>
                      <div>
                        <span style={labelStyle}>Guide Name</span>
                        <div style={{ fontSize: 12, fontWeight: 600 }}>{selectedProposal.guide_name || 'N/A'}</div>
                      </div>
                    </div>

                    {selectedProposal.reference_document && (
                      <div>
                        <span style={labelStyle}>Reference Document</span>
                        <a href={getFullUrl(selectedProposal.reference_document)} target="_blank" rel="noopener noreferrer" style={{ fontSize: 12, color: ACCENT, textDecoration: 'none', fontWeight: 600 }}>
                          View Reference Document ↗
                        </a>
                      </div>
                    )}

                    <div style={{ borderTop: '1px solid #f1f5f9', paddingTop: '10px', fontSize: '11px', color: '#64748b' }}>
                      <div>Status: <strong style={{ color: selectedProposal.status === 'APPROVED' ? '#16a34a' : selectedProposal.status === 'REJECTED' ? '#dc2626' : '#ea580c' }}>{selectedProposal.status}</strong></div>
                      {selectedProposal.reviewed_by_name && (
                        <div style={{ marginTop: '2px' }}>Reviewed By: {selectedProposal.reviewed_by_name}</div>
                      )}
                      {selectedProposal.reviewed_at && (
                        <div style={{ marginTop: '2px' }}>Reviewed At: {new Date(selectedProposal.reviewed_at).toLocaleString('en-IN')}</div>
                      )}
                      {selectedProposal.remarks && (
                        <div style={{ marginTop: '6px', background: '#f8fafc', padding: '8px', borderRadius: '6px', border: '1px solid #e2e8f0', color: '#334155' }}>
                          Remarks: {selectedProposal.remarks}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </>
      )}

      {/* Document Preview Modal */}
      {previewDoc && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 1100, display: 'flex', flexDirection: 'column' }}
          onClick={() => setPreviewDoc(null)}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 20px', background: '#1e293b' }}
            onClick={e => e.stopPropagation()}>
            <span style={{ color: '#fff', fontWeight: 700, fontSize: 14 }}>{previewDoc.file_name}</span>
            <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
              <button onClick={() => setZoom(z => Math.max(0.5, z - 0.25))} style={{ background: '#334155', border: 'none', color: '#fff', borderRadius: 6, padding: '4px 10px', cursor: 'pointer', fontSize: 16 }}>−</button>
              <span style={{ color: '#94a3b8', fontSize: 13 }}>{Math.round(zoom * 100)}%</span>
              <button onClick={() => setZoom(z => Math.min(3, z + 0.25))} style={{ background: '#334155', border: 'none', color: '#fff', borderRadius: 6, padding: '4px 10px', cursor: 'pointer', fontSize: 16 }}>+</button>
              <button onClick={() => setPreviewDoc(null)} style={{ background: '#ef4444', border: 'none', color: '#fff', borderRadius: 6, padding: '4px 12px', cursor: 'pointer', fontWeight: 700, fontSize: 14 }}>✕ Close</button>
            </div>
          </div>
          <div style={{ flex: 1, overflow: 'auto', display: 'flex', justifyContent: 'center', alignItems: 'flex-start', padding: 20 }}
            onClick={e => e.stopPropagation()}>
            {/\.(jpg|jpeg|png|gif|webp)/i.test(previewDoc.file_url || '') ? (
              <img src={getFullUrl(previewDoc.file_url)} alt={previewDoc.file_name}
                style={{ transform: `scale(${zoom})`, transformOrigin: 'top center', maxWidth: '100%' }} />
            ) : (
              <iframe src={`${getFullUrl(previewDoc.file_url)}?t=${new Date().getTime()}`} title={previewDoc.file_name}
                style={{ width: `${100 * zoom}%`, height: `${90 * zoom}vh`, border: 'none', borderRadius: 8, minWidth: 400 }} />
            )}
          </div>
        </div>
      )}
    </div>
  );
}



// ══════════════════════════════════════════════════════════════════════════════
// TAB: Paper Evaluator Approvals
// ══════════════════════════════════════════════════════════════════════════════
function EvaluatorApprovalsTab() {
  const [approvals, setApprovals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [rejectionUser, setRejectionUser] = useState(null);
  const [rejectionReason, setRejectionReason] = useState('');
  const [processing, setProcessing] = useState(false);

  const load = () => {
    setLoading(true);
    coeAPI.getPaperEvaluatorApprovals()
      .then(d => setApprovals(Array.isArray(d) ? d : []))
      .catch(() => setApprovals([]))
      .finally(() => setLoading(false));
  };

  useEffect(load, []);

  const handleApprove = async (id) => {
    if (!await confirmDialog('Approve this Paper Evaluator account?')) return;
    setProcessing(true);
    try {
      await coeAPI.approveRejectPaperEvaluator(id, 'approve');
      notify.success('Paper Evaluator approved successfully.');
      load();
    } catch (err) {
      notify.error(err.response?.data?.error || 'Failed to approve.');
    } finally {
      setProcessing(false);
    }
  };

  const handleRejectSubmit = async () => {
    if (!rejectionReason.trim()) return;
    setProcessing(true);
    try {
      await coeAPI.approveRejectPaperEvaluator(rejectionUser.id, 'reject', rejectionReason);
      notify.success('Paper Evaluator application rejected.');
      setRejectionUser(null);
      setRejectionReason('');
      load();
    } catch (err) {
      notify.error(err.response?.data?.error || 'Failed to reject.');
    } finally {
      setProcessing(false);
    }
  };

  const filtered = approvals.filter(u => {
    const q = search.toLowerCase();
    const fullName = `${u.first_name || ''} ${u.last_name || ''}`.toLowerCase();
    const university = (u.university || '').toLowerCase();
    const state = (u.state || '').toLowerCase();
    const email = (u.email || '').toLowerCase();
    const username = (u.username || '').toLowerCase();
    return fullName.includes(q) || university.includes(q) || state.includes(q) || email.includes(q) || username.includes(q);
  });

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h2 className="text-xl font-bold text-slate-800">Paper Evaluator Registrations</h2>
          <p className="text-sm text-slate-500">Approve or reject evaluator applications. Only approved evaluators can log in.</p>
        </div>
        <div className="w-full md:w-80">
          <input
            type="text"
            className="w-full px-4 py-2 border border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-[#319ab9] focus:outline-none"
            placeholder="🔍 Search name, email, state, university..."
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
        </div>
      </div>

      {loading ? (
        <div className="text-center py-12 text-slate-400">Loading applications...</div>
      ) : filtered.length === 0 ? (
        <div className="bg-white border border-slate-200 rounded-2xl p-12 text-center text-slate-400">
          {search ? 'No matches found for your search.' : 'No pending Paper Evaluator registrations.'}
        </div>
      ) : (
        <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden shadow-sm">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-200 text-slate-600 text-xs font-bold uppercase tracking-wider">
                <th className="px-6 py-4">Evaluator Details</th>
                <th className="px-6 py-4">University</th>
                <th className="px-6 py-4">State</th>
                <th className="px-6 py-4">Status</th>
                <th className="px-6 py-4 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 text-sm text-slate-700">
              {filtered.map(u => (
                <tr key={u.id} className="hover:bg-slate-50/55 transition-colors">
                  <td className="px-6 py-4">
                    <div className="font-semibold text-slate-900">{u.first_name} {u.last_name}</div>
                    <div className="text-xs text-slate-500">{u.email || u.username}</div>
                  </td>
                  <td className="px-6 py-4 text-slate-600">{u.university || '—'}</td>
                  <td className="px-6 py-4">
                    <span className="bg-slate-100 text-slate-800 text-xs px-2.5 py-1 rounded-full font-medium">
                      📍 {u.state || '—'}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold ${
                      u.approval_status === 'APPROVED' ? 'bg-green-100 text-green-800' :
                      u.approval_status === 'REJECTED' ? 'bg-red-100 text-red-800' :
                      'bg-amber-100 text-amber-800'
                    }`}>
                      {u.approval_status}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-right">
                    <div className="flex justify-end gap-2">
                      <button
                        onClick={() => handleApprove(u.id)}
                        disabled={processing}
                        className="bg-green-600 hover:bg-green-700 text-white font-bold px-3.5 py-1.5 rounded-lg text-xs transition-colors shadow-sm disabled:opacity-50"
                      >
                        ✓ Approve
                      </button>
                      <button
                        onClick={() => setRejectionUser(u)}
                        disabled={processing}
                        className="bg-red-50 hover:bg-red-100 text-red-600 font-bold px-3.5 py-1.5 rounded-lg text-xs transition-colors border border-red-200 disabled:opacity-50"
                      >
                        ✕ Reject
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Rejection Modal */}
      {rejectionUser && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4 z-[2000]">
          <div className="bg-white rounded-2xl max-w-md w-full p-6 shadow-xl border border-slate-100">
            <h3 className="text-lg font-bold text-slate-900 mb-2">Reject Registration</h3>
            <p className="text-sm text-slate-500 mb-4">
              Enter a rejection reason for <strong>{rejectionUser.first_name} {rejectionUser.last_name}</strong>.
            </p>
            <textarea
              className="w-full p-3 border border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-red-500 focus:outline-none mb-4"
              rows={3}
              placeholder="Provide reason for rejection (mandatory)..."
              value={rejectionReason}
              onChange={e => setRejectionReason(e.target.value)}
            />
            <div className="flex justify-end gap-3">
              <button
                onClick={() => { setRejectionUser(null); setRejectionReason(''); }}
                className="px-4 py-2 text-sm font-semibold text-slate-600 bg-slate-100 hover:bg-slate-200 rounded-xl transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleRejectSubmit}
                disabled={processing || !rejectionReason.trim()}
                className="px-4 py-2 text-sm font-semibold text-white bg-red-600 hover:bg-red-700 rounded-xl transition-colors disabled:opacity-50"
              >
                Reject Application
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// TAB: Assign Answer Sheets (Bundles & Evaluators)
// ══════════════════════════════════════════════════════════════════════════════
function AssignAnswerSheetsTab() {
  const [bundles, setBundles] = useState([]);
  const [timetables, setTimetables] = useState([]);
  const [selectedTimetable, setSelectedTimetable] = useState('');
  const [schedules, setSchedules] = useState([]);
  const [selectedQP, setSelectedQP] = useState('');
  const [selectedStudents, setSelectedStudents] = useState(new Set());
  const [evaluators, setEvaluators] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState('');
  const [selectedEvaluatorIds, setSelectedEvaluatorIds] = useState([]);
  const [viewStudentModal, setViewStudentModal] = useState(null); // { bundle, studentAssignment }
  const [activeVideoUrl, setActiveVideoUrl] = useState(null);
  const [viewExamStudentsModal, setViewExamStudentsModal] = useState(null); // stores bundle b
  const [coeBookletView, setCoeBookletView] = useState(null); // stores { pdfUrl, studentName, studentId, subject, annotations }
  
  // New States for redesigned Group Flow
  const [schedulesData, setSchedulesData] = useState({}); // mapping: scheduleId -> { total, assigned, pending, studentsList, assignedIds }
  const [manageAssignmentsSchedule, setManageAssignmentsSchedule] = useState(null); // stores the schedule object
  const [manageMode, setManageMode] = useState('LIST'); // 'LIST' | 'CREATE' | 'REASSIGN'
  const [questionPapers, setQuestionPapers] = useState([]);

  const handleViewStudentDetails = (bundle, sa) => {
    setViewStudentModal({ bundle, studentAssignment: sa });
  };

  const handleViewExamStudents = (bundle) => {
    setViewExamStudentsModal(bundle);
  };

  const loadData = async () => {
    setLoading(true);
    try {
      const [bData, tData, qpData, eData] = await Promise.all([
        coeAPI.getBundles(),
        coeAPI.getTimetables(),
        coeAPI.getQuestionPapers(),
        coeAPI.getPaperEvaluators()
      ]);
      setBundles(Array.isArray(bData) ? bData : (bData.results || []));
      setTimetables(Array.isArray(tData) ? tData : (tData.results || []));
      setQuestionPapers((Array.isArray(qpData) ? qpData : (qpData.results || [])).filter(q => q.is_finalized));
      setEvaluators(Array.isArray(eData) ? eData : (eData.results || []));
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  // Fetch schedules when timetable changes
  useEffect(() => {
    if (!selectedTimetable) {
      setSchedules([]);
      return;
    }
    coeAPI.getSchedules({ timetable_id: selectedTimetable })
      .then(d => setSchedules(Array.isArray(d) ? d : (d.results || [])))
      .catch(() => setSchedules([]));
  }, [selectedTimetable]);

  // Fetch student lists for all schedules in the selected timetable and compute stats
  useEffect(() => {
    if (schedules.length === 0) {
      setSchedulesData({});
      return;
    }

    const fetchAllScheduleStudents = async () => {
      const dataMap = {};
      await Promise.all(schedules.map(async (s) => {
        try {
          const data = await coeAPI.getScheduleStudents(s.id);
          const list = (Array.isArray(data) ? data : []).filter(sa => sa.answer_sheets && sa.answer_sheets.length > 0);
          
          // Find students in bundles for this schedule
          const scheduleBundles = bundles.filter(b => b.schedule === s.id);
          const assignedIds = new Set();
          scheduleBundles.forEach(b => {
            (b.student_assignments || []).forEach(sa => {
              assignedIds.add(sa.student_assignment_id || sa.id);
            });
          });
          
          const total = list.length;
          const assigned = list.filter(sa => assignedIds.has(sa.id)).length;
          const pending = total - assigned;
          
          dataMap[s.id] = {
            total,
            assigned,
            pending,
            studentsList: list,
            assignedIds
          };
        } catch (err) {
          console.error(err);
          dataMap[s.id] = { total: 0, assigned: 0, pending: 0, studentsList: [], assignedIds: new Set() };
        }
      }));
      setSchedulesData(dataMap);
    };

    fetchAllScheduleStudents();
  }, [schedules, bundles]);

  const toggleStudent = (id) => {
    setSelectedStudents(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleSelectAllStudents = (filteredIds) => {
    setSelectedStudents(new Set(filteredIds));
  };

  const handleClearAllStudents = () => {
    setSelectedStudents(new Set());
  };

  // Re-evaluation of state/evaluators selection
  const getEvaluatorStatesInfo = () => {
    const selected = evaluators.filter(e => selectedEvaluatorIds.includes(e.id));
    const states = selected.map(e => e.state).filter(Boolean);
    const uniqueStates = new Set(states);
    const isConflict = uniqueStates.size < states.length;
    return { selected, states, uniqueStates, isConflict };
  };

  const toggleEvaluatorSelection = (id) => {
    setSelectedEvaluatorIds(prev => {
      if (prev.includes(id)) {
        return prev.filter(x => x !== id);
      }
      if (prev.length >= 3) {
        notify.success('You can assign a maximum of 3 evaluators. Unselect one to choose a different one.');
        return prev;
      }
      return [...prev, id];
    });
  };

  const { isConflict: evaluatorStateConflict } = getEvaluatorStatesInfo();

  const handleCreateAssignmentGroup = async () => {
    if (!manageAssignmentsSchedule || !selectedQP || selectedStudents.size === 0) {
      notify.error('Please select Question Paper and at least 1 Student.');
      return;
    }
    if (selectedEvaluatorIds.length < 2 || selectedEvaluatorIds.length > 3) {
      notify.error('Please select between 2 and 3 Paper Evaluators.');
      return;
    }
    if (evaluatorStateConflict) {
      notify.success('Conflict: The selected evaluators must belong to different states.');
      return;
    }

    setSaving(true);
    try {
      // 1. Create Bundle
      const newBundle = await coeAPI.createBundle(manageAssignmentsSchedule.id, selectedQP, [...selectedStudents]);
      // 2. Assign Evaluators V2
      await coeAPI.assignBundleEvaluatorsV2(newBundle.id, selectedEvaluatorIds);
      
      notify.success('Assignment Group created and evaluators assigned successfully (V2).');
      
      // Reset CREATE form states
      setSelectedQP('');
      setSelectedStudents(new Set());
      setSelectedEvaluatorIds([]);
      setSearch('');
      
      // Reload and return to list
      await loadData();
      setManageMode('LIST');
    } catch (err) {
      notify.error(err.response?.data?.error || 'Failed to create group assignment.');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteBundle = async (id) => {
    if (!await confirmDialog('Delete this Assignment Group? This will clear all evaluator assignments and evaluations. This cannot be undone.')) return;
    try {
      await coeAPI.deleteBundle(id);
      notify.success('Assignment Group deleted.');
      loadData();
    } catch (err) {
      notify.error(err.response?.data?.error || 'Failed to delete group.');
    }
  };

  const handleStudentReassign = async (studentAssignmentId, action, targetBundleId = null) => {
    try {
      setSaving(true);
      const res = await coeAPI.reassignStudent(studentAssignmentId, action, targetBundleId);
      notify.success(res.message || 'Student reassigned successfully.');
      await loadData();
    } catch (err) {
      notify.error(err.response?.data?.error || 'Failed to reassign student.');
    } finally {
      setSaving(false);
    }
  };

  // ── RENDER ──
  return (
    <div className="space-y-6">
      {/* Top Selection Panel */}
      <div className="bg-white border border-slate-200 rounded-2xl p-5 shadow-sm flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div className="flex-1 max-w-md">
          <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">Select Examination Timetable</label>
          <select 
            className="w-full px-4 py-2.5 border border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-[#319ab9] focus:outline-none font-semibold text-slate-700 bg-white"
            value={selectedTimetable} 
            onChange={e => {
              setSelectedTimetable(e.target.value);
              setManageAssignmentsSchedule(null);
            }}
          >
            <option value="">— Choose Timetable —</option>
            {timetables.map(t => <option key={t.id} value={t.id}>{t.title} ({t.course})</option>)}
          </select>
        </div>
        <div className="text-right">
          <h2 className="text-lg font-black text-slate-800">Assign Answer Sheets</h2>
          <p className="text-xs text-slate-500 mt-1">Manage exam assignment groups and state-wise paper evaluator workflows.</p>
        </div>
      </div>

      {loading && <div className="text-center py-12 text-slate-400">Loading schedules and evaluation data...</div>}

      {!loading && !selectedTimetable && (
        <div className="bg-white border border-slate-200 rounded-2xl p-12 text-center text-slate-400">
          <span className="text-3xl block mb-2">📅</span>
          Please select an examination timetable above to view scheduled subjects.
        </div>
      )}

      {!loading && selectedTimetable && schedules.length === 0 && (
        <div className="bg-white border border-slate-200 rounded-2xl p-12 text-center text-slate-400">
          No scheduled examinations found for this timetable.
        </div>
      )}

      {!loading && selectedTimetable && schedules.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {schedules.map(s => {
            const sData = schedulesData[s.id] || { total: 0, assigned: 0, pending: 0 };
            return (
              <div key={s.id} className="bg-white border border-slate-200 rounded-2xl p-5 shadow-sm hover:shadow-md transition-shadow flex flex-col justify-between space-y-4">
                <div>
                  <div className="flex justify-between items-start gap-2">
                    <span className="bg-[#319ab9]/10 text-[#319ab9] text-[10px] font-black px-2.5 py-1 rounded-full uppercase tracking-wider">
                      {s.exam_type_display} | {s.sub_type_display}
                    </span>
                    <span className="text-xs text-slate-400 font-bold">{fmtDate(s.exam_date)}</span>
                  </div>
                  <h3 className="text-base font-extrabold text-slate-900 mt-3">{s.subject}</h3>
                  {s.paper_label && <p className="text-xs text-slate-500 mt-0.5">{s.paper_label}</p>}
                </div>

                <div className="grid grid-cols-3 gap-2 border-t border-slate-100 pt-4 text-center">
                  <div>
                    <div className="text-[10px] font-bold text-slate-400 uppercase tracking-wide">Total Students</div>
                    <div className="text-base font-black text-slate-800 mt-1">{sData.total}</div>
                  </div>
                  <div>
                    <div className="text-[10px] font-bold text-slate-400 uppercase tracking-wide">Assigned</div>
                    <div className="text-base font-black text-green-600 mt-1">{sData.assigned}</div>
                  </div>
                  <div>
                    <div className="text-[10px] font-bold text-slate-400 uppercase tracking-wide">Pending</div>
                    <div className="text-base font-black text-amber-600 mt-1">{sData.pending}</div>
                  </div>
                </div>

                <button
                  onClick={() => {
                    setManageAssignmentsSchedule(s);
                    setManageMode('LIST');
                  }}
                  className="w-full bg-[#319ab9] hover:bg-[#28849f] text-white font-extrabold py-2.5 rounded-xl text-xs transition-colors flex items-center justify-center gap-1.5 shadow-sm mt-2"
                >
                  📦 Manage Assignments
                </button>
              </div>
            );
          })}
        </div>
      )}

      {/* Main Manage Assignments Drawer/Modal */}
      {manageAssignmentsSchedule && (() => {
        const s = manageAssignmentsSchedule;
        const sData = schedulesData[s.id] || { total: 0, assigned: 0, pending: 0, studentsList: [], assignedIds: new Set() };
        const scheduleBundles = bundles.filter(b => b.schedule === s.id);
        
        return (
          <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4 z-[1800] animate-fadeIn">
            <div className="bg-white rounded-2xl max-w-5xl w-full p-6 shadow-2xl border border-slate-100 flex flex-col max-h-[90vh]">
              
              {/* Modal Header */}
              <div className="flex justify-between items-start border-b border-slate-100 pb-4 mb-4">
                <div>
                  <h3 className="text-lg font-black text-slate-900">Manage Assignments — {s.subject}</h3>
                  <p className="text-xs text-slate-500 mt-0.5">
                    {s.exam_type_display} · {s.sub_type_display} {s.paper_label ? `· ${s.paper_label}` : ''}
                  </p>
                </div>
                <button 
                  onClick={() => setManageAssignmentsSchedule(null)}
                  className="text-slate-400 hover:text-slate-600 text-lg font-bold w-8 h-8 flex items-center justify-center rounded-full hover:bg-slate-50"
                >
                  ✕
                </button>
              </div>

              {/* Modal Body depending on Mode */}
              <div className="flex-1 overflow-y-auto pr-2 space-y-6 min-h-[350px]">
                
                {manageMode === 'LIST' && (
                  <div className="space-y-6">
                    {/* Header stats bar */}
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4 bg-slate-50 p-4 border border-slate-100 rounded-2xl shadow-sm">
                      <div className="text-center md:text-left">
                        <div className="text-[10px] font-bold text-slate-400 uppercase tracking-wide">Total Students</div>
                        <div className="text-lg font-black text-slate-800 mt-1">{sData.total}</div>
                      </div>
                      <div className="text-center md:text-left">
                        <div className="text-[10px] font-bold text-slate-400 uppercase tracking-wide">Assigned Students</div>
                        <div className="text-lg font-black text-green-600 mt-1">{sData.assigned}</div>
                      </div>
                      <div className="text-center md:text-left">
                        <div className="text-[10px] font-bold text-slate-400 uppercase tracking-wide">Remaining (Unassigned)</div>
                        <div className="text-lg font-black text-amber-600 mt-1">{sData.pending}</div>
                      </div>
                      <div className="flex justify-center md:justify-end items-center gap-2">
                        <button
                          disabled={sData.pending === 0}
                          onClick={() => {
                            setManageMode('CREATE');
                            setSelectedStudents(new Set());
                            setSelectedEvaluatorIds([]);
                          }}
                          className="bg-[#319ab9] hover:bg-[#28849f] disabled:opacity-50 text-white font-extrabold px-3 py-2 rounded-xl text-xs transition-colors shadow-sm"
                        >
                          ➕ Create Group
                        </button>
                        <button
                          disabled={sData.total === 0 || scheduleBundles.length === 0}
                          onClick={() => setManageMode('REASSIGN')}
                          className="bg-white hover:bg-slate-50 disabled:opacity-50 text-slate-700 font-extrabold px-3 py-2 rounded-xl text-xs border border-slate-200 transition-colors shadow-sm"
                        >
                          🔄 Reassign Students
                        </button>
                      </div>
                    </div>

                    {/* Assignment Groups list */}
                    <div className="space-y-4">
                      <h4 className="text-sm font-bold text-slate-800">Assignment Groups ({scheduleBundles.length})</h4>
                      
                      {scheduleBundles.length === 0 ? (
                        <div className="border border-dashed border-slate-200 rounded-2xl p-12 text-center text-slate-400">
                          No assignment groups created for this examination yet. Click "Create Group" to start.
                        </div>
                      ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          {scheduleBundles.map((b, index) => {
                            const currentEvals = b.evaluator_assignments || [];
                            
                            // Calculate progress percentage
                            let totalEvalsCount = 0;
                            let completedEvalsCount = 0;
                            (b.student_assignments || []).forEach(sa => {
                              const evs = sa.evaluations || [];
                              totalEvalsCount += evs.length;
                              completedEvalsCount += evs.filter(ev => ev.status === 'SUBMITTED' || ev.status === 'COMPLETED').length;
                            });
                            const progressPercent = totalEvalsCount > 0 ? Math.round((completedEvalsCount / totalEvalsCount) * 100) : 0;
                            
                            return (
                              <div key={b.id} className="bg-white border border-slate-200 rounded-2xl p-4 shadow-sm hover:border-slate-300 transition-all flex flex-col justify-between space-y-4">
                                <div className="space-y-3">
                                  <div className="flex justify-between items-center">
                                    <span className="font-extrabold text-slate-800 text-sm">
                                      Assignment Group {String.fromCharCode(65 + index)}
                                    </span>
                                    <span className="bg-[#319ab9]/10 text-[#319ab9] text-[10px] font-black px-2 py-0.5 rounded">
                                      {b.student_assignments?.length || 0} Student(s)
                                    </span>
                                  </div>

                                  {/* Evaluators */}
                                  <div className="space-y-1.5">
                                    <div className="text-[10px] font-bold text-slate-400 uppercase tracking-wide">Assigned Evaluators</div>
                                    {currentEvals.length === 0 ? (
                                      <div className="text-xs text-amber-600 font-semibold">⚠️ No evaluators assigned yet.</div>
                                    ) : (
                                      <div className="flex flex-wrap gap-1.5">
                                        {currentEvals.map(ea => (
                                          <span key={ea.id} className="bg-slate-100 text-slate-800 text-[10px] font-semibold px-2 py-0.5 rounded-full" title={ea.evaluator_name}>
                                            📍 {ea.state} ({ea.evaluator_name})
                                          </span>
                                        ))}
                                      </div>
                                    )}
                                  </div>

                                  {/* Progress bar */}
                                  <div className="space-y-1 mt-2">
                                    <div className="flex justify-between items-center text-[10px] font-bold text-slate-500">
                                      <span>EVALUATION PROGRESS</span>
                                      <span>{completedEvalsCount}/{totalEvalsCount} ({progressPercent}%)</span>
                                    </div>
                                    <div className="w-full bg-slate-100 h-2 rounded-full overflow-hidden">
                                      <div 
                                        className="bg-green-500 h-full rounded-full transition-all duration-300"
                                        style={{ width: `${progressPercent}%` }}
                                      />
                                    </div>
                                  </div>
                                </div>

                                <div className="flex gap-2 pt-2 border-t border-slate-100 mt-2">
                                  <button
                                    onClick={() => handleViewExamStudents(b)}
                                    className="flex-1 bg-slate-50 hover:bg-slate-100 text-slate-700 font-bold py-2 rounded-xl text-xs border border-slate-200 transition-colors flex items-center justify-center gap-1 shadow-sm"
                                  >
                                    👁 View Students
                                  </button>
                                  {completedEvalsCount === 0 && (
                                    <button
                                      onClick={() => handleDeleteBundle(b.id)}
                                      className="text-slate-400 hover:text-red-500 hover:bg-red-50 p-2 rounded-xl border border-transparent hover:border-red-100 transition-colors"
                                      title="Delete Group"
                                    >
                                      ✕
                                    </button>
                                  )}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {manageMode === 'CREATE' && (() => {
                  const unassignedStudentsList = sData.studentsList.filter(sa => !sData.assignedIds.has(sa.id));
                  
                  // Filter unassigned list by search text
                  const filteredUnassigned = unassignedStudentsList.filter(sa => {
                    const q = search.toLowerCase();
                    const name = (sa.student_name || '').toLowerCase();
                    const idNum = (sa.student_id_num || sa.roll_number || '').toLowerCase();
                    return name.includes(q) || idNum.includes(q);
                  });

                  return (
                    <div className="space-y-6">
                      <div className="flex justify-between items-center border-b border-slate-100 pb-3">
                        <span className="font-bold text-slate-800 text-sm">Create New Assignment Group</span>
                        <button 
                          onClick={() => { setManageMode('LIST'); setSearch(''); }}
                          className="text-xs font-bold text-slate-500 hover:text-slate-800 bg-slate-50 px-3 py-1 rounded-lg border border-slate-200"
                        >
                          ← Back to Groups
                        </button>
                      </div>

                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                        
                        {/* Left Panel: Unassigned Students list */}
                        <div className="bg-slate-50 border border-slate-200/60 rounded-2xl p-4 flex flex-col max-h-[50vh]">
                          <div className="flex flex-col md:flex-row md:items-center justify-between gap-3 border-b border-slate-200/60 pb-3 mb-3">
                            <div>
                              <h5 className="font-extrabold text-slate-800 text-xs uppercase tracking-wider">Unassigned Students ({selectedStudents.size} selected)</h5>
                              <p className="text-[10px] text-slate-500 mt-0.5">Students who sit this exam and are not in another group.</p>
                            </div>
                            <div className="flex gap-2">
                              <button 
                                onClick={() => handleSelectAllStudents(filteredUnassigned.map(x => x.id))} 
                                className="bg-white hover:bg-slate-100 text-slate-700 text-[10px] font-bold px-2.5 py-1.5 rounded-lg border border-slate-200 shadow-sm"
                              >
                                Select All
                              </button>
                              <button 
                                onClick={handleClearAllStudents} 
                                className="bg-white hover:bg-slate-100 text-slate-700 text-[10px] font-bold px-2.5 py-1.5 rounded-lg border border-slate-200 shadow-sm"
                              >
                                Clear
                              </button>
                              <input
                                type="text"
                                className="px-2.5 py-1 border border-slate-200 rounded-lg text-xs focus:ring-2 focus:ring-[#319ab9] focus:outline-none bg-white w-40"
                                placeholder="Search..."
                                value={search}
                                onChange={e => setSearch(e.target.value)}
                              />
                            </div>
                          </div>

                          <div className="flex-1 overflow-y-auto space-y-2 pr-1">
                            {filteredUnassigned.length === 0 ? (
                              <div className="text-center py-12 text-slate-400 text-xs italic">
                                {unassignedStudentsList.length === 0 ? 'All student answer sheets already assigned!' : 'No matching unassigned students.'}
                              </div>
                            ) : (
                              filteredUnassigned.map(sa => {
                                const isChecked = selectedStudents.has(sa.id);
                                return (
                                  <div
                                    key={sa.id}
                                    onClick={() => toggleStudent(sa.id)}
                                    className={`flex items-center gap-3 p-2.5 border rounded-xl cursor-pointer transition-all ${
                                      isChecked ? 'bg-[#319ab9]/10 border-[#319ab9]' : 'bg-white border-slate-200 hover:border-slate-300'
                                    }`}
                                  >
                                    <input
                                      type="checkbox"
                                      checked={isChecked}
                                      onChange={() => {}}
                                      className="rounded text-[#319ab9] focus:ring-[#319ab9] w-4 h-4 accent-[#319ab9]"
                                    />
                                    <div className="min-w-0 flex-1">
                                      <div className="text-xs font-bold text-slate-800 truncate">{sa.student_name}</div>
                                      <div className="text-[10px] text-slate-400 font-semibold">REG: {sa.student_id_num || sa.roll_number || '—'}</div>
                                    </div>
                                  </div>
                                );
                              })
                            )}
                          </div>
                        </div>

                        {/* Right Panel: QP & Evaluator selections */}
                        <div className="space-y-4">
                          
                          {/* QP select */}
                          <div className="bg-white border border-slate-200 rounded-2xl p-4 shadow-sm space-y-2">
                            <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-wide">Select Finalized Question Paper</label>
                            <select 
                              className="w-full px-3 py-2 border border-slate-200 rounded-xl text-xs focus:ring-2 focus:ring-[#319ab9] focus:outline-none font-semibold text-slate-700 bg-white"
                              value={selectedQP}
                              onChange={e => setSelectedQP(e.target.value)}
                            >
                              <option value="">— Choose Question Paper —</option>
                              {questionPapers.map(qp => {
                                const computedMarks = qp.total_marks > 0
                                  ? qp.total_marks
                                  : (qp.sections || []).reduce((sum, sec) => {
                                      const q = sec.attempt_questions > 0 ? sec.attempt_questions : (sec.total_questions || 0);
                                      return sum + (parseInt(sec.marks_per_q || 0)) * q;
                                    }, 0);
                                return (
                                  <option key={qp.id} value={qp.id}>
                                    {qp.title} ({computedMarks} Marks)
                                  </option>
                                );
                              })}
                            </select>
                          </div>

                          {/* Evaluators select & validation */}
                          <div className="bg-white border border-slate-200 rounded-2xl p-4 shadow-sm space-y-4">
                            <div>
                              <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-wide mb-1">Select Evaluators (Evaluator 3 is Optional)</label>
                              <p className="text-[10px] text-slate-500">Minimum 2 evaluators required. Each must be from a different state.</p>
                            </div>

                            {/* Validation warning banner */}
                            <div className={`p-3 border rounded-xl text-[10px] font-bold flex items-center gap-2 ${
                              selectedEvaluatorIds.length < 2 ? 'bg-slate-50 border-slate-200 text-slate-500' :
                              evaluatorStateConflict ? 'bg-red-50 border-red-200 text-red-700' :
                              selectedEvaluatorIds.length === 2 ? 'bg-amber-50 border-amber-200 text-amber-700' :
                              'bg-green-50 border-green-200 text-green-700'
                            }`}>
                              {selectedEvaluatorIds.length < 2 ? (
                                <span>⚠️ Choose at least 2 evaluators ({selectedEvaluatorIds.length} selected)</span>
                              ) : evaluatorStateConflict ? (
                                <span>❌ State Conflict: Multiple selected evaluators belong to the same state.</span>
                              ) : selectedEvaluatorIds.length === 2 ? (
                                <span>✓ 2 evaluators selected. You may optionally add a 3rd evaluator.</span>
                              ) : (
                                <span>✓ All 3 evaluators belong to different states.</span>
                              )}
                            </div>

                            {/* Evaluators list check list */}
                            <div className="max-h-[30vh] overflow-y-auto space-y-2 pr-1">
                              {evaluators.length === 0 ? (
                                <div className="text-center py-6 text-slate-400 text-xs italic">No approved Paper Evaluators available in system.</div>
                              ) : (
                                evaluators.map(e => {
                                  const isChecked = selectedEvaluatorIds.includes(e.id);
                                  return (
                                    <div
                                      key={e.id}
                                      onClick={() => toggleEvaluatorSelection(e.id)}
                                      className={`flex items-center justify-between p-2.5 border rounded-xl cursor-pointer transition-all ${
                                        isChecked ? 'bg-[#319ab9]/10 border-[#319ab9]' : 'bg-white border-slate-200 hover:border-slate-300'
                                      }`}
                                    >
                                      <div className="flex items-center gap-2.5">
                                        <input
                                          type="checkbox"
                                          checked={isChecked}
                                          onChange={() => {}}
                                          className="rounded text-[#319ab9] focus:ring-[#319ab9] w-3.5 h-3.5 accent-[#319ab9]"
                                        />
                                        <div className="min-w-0">
                                          <div className="text-xs font-bold text-slate-800">{e.first_name} {e.last_name}</div>
                                          <div className="text-[10px] text-slate-400">{e.university || 'External Board'}</div>
                                        </div>
                                      </div>
                                      <span className="bg-slate-100 text-slate-700 text-[10px] px-2 py-0.5 rounded-full font-bold">
                                        📍 {e.state}
                                      </span>
                                    </div>
                                  );
                                })
                              )}
                            </div>
                          </div>
                        </div>

                      </div>

                      {/* Bottom Create Button */}
                      <div className="border-t border-slate-100 pt-4 flex justify-end gap-3">
                        <button
                          onClick={() => { setManageMode('LIST'); setSelectedQP(''); setSelectedStudents(new Set()); setSelectedEvaluatorIds([]); setSearch(''); }}
                          className="px-4 py-2 text-xs font-semibold text-slate-600 bg-slate-100 hover:bg-slate-200 rounded-xl transition-colors"
                        >
                          Cancel
                        </button>
                        <button
                          onClick={handleCreateAssignmentGroup}
                          disabled={saving || selectedStudents.size === 0 || !selectedQP || selectedEvaluatorIds.length < 2 || evaluatorStateConflict}
                          className="px-6 py-2 text-xs font-extrabold text-white bg-[#319ab9] hover:bg-[#28849f] rounded-xl transition-colors disabled:opacity-50 shadow-sm"
                        >
                          {saving ? 'Creating...' : '💾 Create Assignment Group'}
                        </button>
                      </div>
                    </div>
                  );
                })()}

                {manageMode === 'REASSIGN' && (() => {
                  return (
                    <div className="space-y-6 animate-fadeIn">
                      <div className="flex justify-between items-center border-b border-slate-100 pb-3">
                        <div>
                          <span className="font-bold text-slate-800 text-sm">🔄 Reassign Students</span>
                          <p className="text-[10px] text-slate-500">Safely add, remove or move students between groups. Duplicate assignments are prohibited.</p>
                        </div>
                        <button 
                          onClick={() => setManageMode('LIST')}
                          className="text-xs font-bold text-slate-500 hover:text-slate-800 bg-slate-50 px-3 py-1 rounded-lg border border-slate-200"
                        >
                          ← Back to Groups
                        </button>
                      </div>

                      <div className="border border-slate-200 rounded-2xl overflow-hidden bg-white shadow-sm">
                        <table className="w-full text-left border-collapse">
                          <thead>
                            <tr className="bg-slate-50 border-b border-slate-200 text-slate-500 text-[10px] font-bold uppercase tracking-wider">
                              <th className="px-5 py-3">Student Name</th>
                              <th className="px-5 py-3">REG (Roll No)</th>
                              <th className="px-5 py-3">Current Assignment Group</th>
                              <th className="px-5 py-3 text-right">Actions</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-slate-100 text-xs text-slate-700">
                            {sData.studentsList.map(student => {
                              // Find which group/bundle they belong to
                              const studentBundle = scheduleBundles.find(b => 
                                (b.student_assignments || []).some(sa => (sa.student_assignment_id || sa.id) === student.id)
                              );
                              
                              const groupIndex = studentBundle ? scheduleBundles.indexOf(studentBundle) : -1;
                              const currentGroupName = studentBundle ? `Group ${String.fromCharCode(65 + groupIndex)}` : 'Unassigned';

                              return (
                                <tr key={student.id} className="hover:bg-slate-50/50">
                                  <td className="px-5 py-3.5 font-bold text-slate-800">{student.student_name}</td>
                                  <td className="px-5 py-3.5 text-slate-500">{student.student_id_num || student.roll_number || '—'}</td>
                                  <td className="px-5 py-3.5">
                                    <span className={`px-2.5 py-0.5 rounded font-black ${
                                      studentBundle ? 'bg-green-100 text-green-800' : 'bg-amber-100 text-amber-800'
                                    }`}>
                                      {currentGroupName}
                                    </span>
                                  </td>
                                  <td className="px-5 py-3.5 text-right">
                                    <div className="flex justify-end items-center gap-2">
                                      {studentBundle ? (
                                        <>
                                          {/* Move to another group select dropdown */}
                                          <select
                                            disabled={saving}
                                            className="px-2 py-1.5 border border-slate-200 rounded-lg bg-white text-[11px] focus:ring-1 focus:ring-[#319ab9] focus:outline-none"
                                            value=""
                                            onChange={e => {
                                              if (e.target.value) {
                                                handleStudentReassign(student.id, 'MOVE', e.target.value);
                                              }
                                            }}
                                          >
                                            <option value="">Move to...</option>
                                            {scheduleBundles.map((b, idx) => {
                                              if (b.id === studentBundle.id) return null;
                                              return (
                                                <option key={b.id} value={b.id}>
                                                  Group {String.fromCharCode(65 + idx)}
                                                </option>
                                              );
                                            })}
                                          </select>

                                          <button
                                            disabled={saving}
                                            onClick={() => handleStudentReassign(student.id, 'REMOVE')}
                                            className="bg-red-50 hover:bg-red-100 text-red-600 font-bold px-3 py-1.5 rounded-lg border border-red-200 transition-colors"
                                          >
                                            Remove
                                          </button>
                                        </>
                                      ) : (
                                        /* Assign to dropdown */
                                        <select
                                          disabled={saving || scheduleBundles.length === 0}
                                          className="px-2 py-1.5 border border-slate-200 rounded-lg bg-white text-[11px] focus:ring-1 focus:ring-[#319ab9] focus:outline-none disabled:opacity-50"
                                          value=""
                                          onChange={e => {
                                            if (e.target.value) {
                                              handleStudentReassign(student.id, 'MOVE', e.target.value);
                                            }
                                          }}
                                        >
                                          <option value="">Assign to...</option>
                                          {scheduleBundles.map((b, idx) => (
                                            <option key={b.id} value={b.id}>
                                              Group {String.fromCharCode(65 + idx)}
                                            </option>
                                          ))}
                                        </select>
                                      )}
                                    </div>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  );
                })()}

              </div>

              {/* Modal Footer */}
              <div className="border-t border-slate-100 pt-4 mt-4 flex justify-end">
                <button
                  onClick={() => setManageAssignmentsSchedule(null)}
                  className="px-6 py-2 text-sm font-semibold text-slate-600 bg-slate-100 hover:bg-slate-200 rounded-xl transition-colors"
                >
                  Close Manager
                </button>
              </div>

            </div>
          </div>
        );
      })()}

      {/* Detailed Student Information Modal (Reused) */}
      {viewStudentModal && (() => {
        const { bundle, studentAssignment } = viewStudentModal;
        const evals = studentAssignment.evaluations || [];
        const completedCount = evals.filter(ev => ev.status === 'SUBMITTED' || ev.status === 'COMPLETED').length;
        const pendingCount = evals.length - completedCount;
        
        return (
          <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4 z-[2000] animate-fadeIn">
            <div className="bg-white rounded-2xl max-w-4xl w-full p-6 shadow-xl border border-slate-100 flex flex-col max-h-[90vh]">
              
              {/* Header */}
              <div className="flex justify-between items-start border-b border-slate-100 pb-4 mb-4">
                <div>
                  <h3 className="text-lg font-bold text-slate-900">Student Evaluation Information Panel</h3>
                  <p className="text-xs text-slate-500">Comprehensive view of student evaluation status, marks, and recorded session videos.</p>
                </div>
                <button 
                  onClick={() => setViewStudentModal(null)} 
                  className="text-slate-400 hover:text-slate-600 text-lg font-bold w-8 h-8 flex items-center justify-center rounded-full hover:bg-slate-50"
                >
                  ✕
                </button>
              </div>

              {/* Scrollable Body Container */}
              <div className="flex-1 overflow-y-auto pr-2 space-y-6">
                
                {/* Status Summary Cards */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="bg-slate-50 border border-slate-100 rounded-xl p-3.5 flex flex-col justify-between shadow-sm">
                    <span className="text-slate-500 text-[10px] font-bold uppercase tracking-wider">Evaluation Status</span>
                    <span className={`text-sm font-extrabold mt-1.5 ${completedCount === 3 ? 'text-green-600' : 'text-amber-600'}`}>
                      {completedCount === 3 ? '✓ Fully Completed' : '⏳ In Progress'}
                    </span>
                  </div>
                  <div className="bg-slate-50 border border-slate-100 rounded-xl p-3.5 flex flex-col justify-between shadow-sm">
                    <span className="text-slate-500 text-[10px] font-bold uppercase tracking-wider">Completed Evaluations</span>
                    <span className="text-base font-extrabold text-slate-950 mt-1.5">{completedCount} / 3 Evaluators</span>
                  </div>
                  <div className="bg-slate-50 border border-slate-100 rounded-xl p-3.5 flex flex-col justify-between shadow-sm">
                    <span className="text-slate-500 text-[10px] font-bold uppercase tracking-wider">Pending Evaluations</span>
                    <span className="text-base font-extrabold text-slate-950 mt-1.5">{pendingCount} Evaluator(s)</span>
                  </div>
                </div>

                {/* Main 2-Column Grid */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  
                  {/* Left Column: Student & Paper metadata */}
                  <div className="space-y-5">
                    
                    {/* Student Info Card */}
                    <div className="bg-slate-50/50 border border-slate-200/60 rounded-2xl p-5 space-y-3 shadow-sm">
                      <h4 className="font-extrabold text-slate-800 text-xs uppercase tracking-wider border-b border-slate-200/60 pb-2">Student & Exam Metadata</h4>
                      
                      <div className="grid grid-cols-2 gap-y-3 text-xs">
                        <div>
                          <div className="text-slate-400 font-semibold">Student Name</div>
                          <div className="font-bold text-slate-900 mt-0.5">{studentAssignment.student_name}</div>
                        </div>
                        <div>
                          <div className="text-slate-400 font-semibold">Student REG (Roll No)</div>
                          <div className="font-bold text-slate-900 mt-0.5">{studentAssignment.student_id_num || studentAssignment.roll_number || '—'}</div>
                        </div>
                        <div>
                          <div className="text-slate-400 font-semibold">Subject</div>
                          <div className="font-bold text-slate-900 mt-0.5">{bundle.schedule_details?.subject || '—'}</div>
                        </div>
                        <div>
                          <div className="text-slate-400 font-semibold">Department</div>
                          <div className="font-bold text-slate-900 mt-0.5">{bundle.schedule_details?.department || '—'}</div>
                        </div>
                        <div>
                          <div className="text-slate-400 font-semibold">Paper Number</div>
                          <div className="font-bold text-slate-900 mt-0.5">{bundle.schedule_details?.paper_number || '—'}</div>
                        </div>
                      </div>
                    </div>

                    {/* Question Paper & Answer Sheets Card */}
                    <div className="bg-slate-50/50 border border-slate-200/60 rounded-2xl p-5 space-y-4 shadow-sm">
                      <div>
                        <h4 className="font-extrabold text-slate-800 text-xs uppercase tracking-wider border-b border-slate-200/60 pb-2 mb-2">Question Paper</h4>
                        <div className="text-xs flex items-center justify-between">
                          <span className="font-bold text-slate-800">{bundle.question_paper_details?.title || '—'}</span>
                          <span className="bg-[#319ab9]/10 text-[#319ab9] font-extrabold px-2.5 py-1 rounded text-xs">
                            {bundle.question_paper_details?.total_marks} Marks
                          </span>
                        </div>
                      </div>

                      <div>
                        <h4 className="font-extrabold text-slate-800 text-xs uppercase tracking-wider border-b border-slate-200/60 pb-2 mb-2">Uploaded Answer Sheets</h4>
                        <div className="space-y-2">
                          {(!studentAssignment.answer_sheets || studentAssignment.answer_sheets.length === 0) ? (
                            <div className="text-xs text-slate-400 italic">No answer sheets uploaded for this student.</div>
                          ) : (
                            studentAssignment.answer_sheets.map((sheet, index) => (
                              <a
                                key={sheet.id || index}
                                href={getFullUrl(sheet.file)}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="flex items-center gap-2.5 p-2 bg-white border border-slate-200 rounded-xl hover:border-[#319ab9] hover:bg-slate-50/55 transition-all text-xs font-semibold text-slate-700 shadow-sm"
                              >
                                <span className="text-base">📎</span>
                                <span className="truncate flex-1">{sheet.file_name || `Answer Sheet #${index + 1}`}</span>
                                <span className="text-[10px] text-[#319ab9] underline">View File</span>
                              </a>
                            ))
                          )}
                        </div>
                      </div>
                    </div>

                  </div>

                  {/* Right Column: Evaluators & Video Recordings */}
                  <div className="space-y-4">
                    <h4 className="font-extrabold text-slate-800 text-xs uppercase tracking-wider">Assigned Evaluator Marks & Videos</h4>
                    
                    {evals.length === 0 ? (
                      <div className="text-xs text-slate-400 italic bg-slate-50 border border-slate-200 rounded-xl p-4 text-center">
                        No Paper Evaluators assigned to this bundle yet.
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {evals.map((ev, index) => {
                          const isComplete = ev.status === 'SUBMITTED' || ev.status === 'COMPLETED';
                          const isSaved = ev.status === 'SAVED';
                          
                          return (
                            <div key={ev.id || index} className="bg-slate-50 border border-slate-200 rounded-2xl p-4 space-y-3 shadow-sm">
                              
                              {/* Evaluator name & State badge */}
                              <div className="flex justify-between items-start gap-2">
                                <div>
                                  <div className="text-xs font-black text-slate-800 font-bold">Evaluator {index + 1}: {ev.evaluator_name}</div>
                                  <div className="text-[10px] text-slate-500 font-semibold mt-0.5">📍 {ev.evaluator_state || 'State not set'}</div>
                                </div>
                                <span className={`text-[10px] font-black px-2 py-0.5 rounded-full ${
                                  isComplete ? 'bg-green-100 text-green-800' :
                                  isSaved ? 'bg-blue-100 text-blue-800' :
                                  'bg-amber-100 text-amber-800'
                                }`}>
                                  {isComplete ? 'Completed' : isSaved ? 'Draft Saved' : 'Pending'}
                                </span>
                              </div>

                              {/* Marks breakdown */}
                              <div className="text-xs space-y-1">
                                {isComplete || isSaved ? (
                                  <>
                                    <div className="flex justify-between items-center bg-white p-2 rounded-xl border border-slate-200">
                                      <span className="font-semibold text-slate-600">Total Score Given</span>
                                      <span className="font-black text-[#319ab9] font-extrabold">{ev.total_marks} Marks</span>
                                    </div>
                                    <div className="bg-white p-2.5 rounded-xl border border-slate-200 mt-2 space-y-1.5">
                                      <div className="text-[10px] font-bold text-slate-400 uppercase tracking-wide mb-1 border-b border-slate-100 pb-1">Section-wise Marks</div>
                                      {(!ev.section_marks || Object.keys(ev.section_marks).length === 0) ? (
                                        <div className="text-[10px] text-slate-400 italic">No section marks found.</div>
                                      ) : (
                                        Object.entries(ev.section_marks).map(([sectionId, secScore]) => {
                                          const sectionObj = (bundle.question_paper_details?.sections || []).find(sec => String(sec.id) === String(sectionId));
                                          const sectionTitle = sectionObj ? sectionObj.title : `Section ID ${sectionId}`;
                                          return (
                                            <div key={sectionId} className="flex justify-between text-[11px]">
                                              <span className="text-slate-600 font-semibold">{sectionTitle}</span>
                                              <span className="font-black text-slate-800 font-bold">{secScore} Marks</span>
                                            </div>
                                          );
                                        })
                                      )}
                                    </div>
                                    {ev.comments && (
                                      <div className="bg-amber-50/40 p-2.5 rounded-xl border border-amber-200/40 mt-2">
                                        <div className="text-[10px] font-bold text-amber-800">Remarks:</div>
                                        <div className="text-[11px] text-slate-600 italic mt-0.5">{ev.comments}</div>
                                      </div>
                                    )}
                                  </>
                                ) : (
                                  <div className="text-slate-400 italic">Marks will be displayed once the evaluation is draft-saved or submitted.</div>
                                )}
                              </div>

                              {/* Video Playback & Booklet Viewer actions */}
                              <div className="pt-2 border-t border-slate-200/60 flex items-center justify-between gap-3">
                                {(isComplete || isSaved) ? (
                                  <button
                                    onClick={() => setCoeBookletView({
                                      pdfUrl: ev.evaluated_pdf ? getFullUrl(ev.evaluated_pdf) : (studentAssignment.combined_pdf_url ? getFullUrl(studentAssignment.combined_pdf_url) : null),
                                      studentName: studentAssignment.student_name,
                                      studentId: studentAssignment.student_id_num,
                                      subject: bundle.schedule_details?.subject || '',
                                      annotations: ev.annotations || {}
                                    })}
                                    className="bg-green-600 hover:bg-green-700 text-white font-bold px-3 py-1.5 rounded-xl text-xs transition-colors flex items-center gap-1.5 shadow-sm"
                                  >
                                    <span>👁</span> View Evaluated Booklet
                                  </button>
                                ) : (
                                  <span className="text-[11px] text-slate-400 italic">Not evaluated yet</span>
                                )}

                                <div className="flex gap-2">
                                  {ev.video_recording ? (
                                    <button
                                      onClick={() => setActiveVideoUrl(ev.video_recording)}
                                      className="bg-[#319ab9] hover:bg-[#28849f] text-white font-bold px-3 py-1.5 rounded-xl text-xs transition-colors flex items-center gap-1.5 shadow-sm"
                                    >
                                      <span>▶</span> View Evaluation Video
                                    </button>
                                  ) : (
                                    <button
                                      disabled
                                      className="bg-slate-100 text-slate-400 font-semibold px-3 py-1.5 rounded-xl text-xs cursor-not-allowed flex items-center gap-1.5 border border-slate-200"
                                    >
                                      <span>🎥</span> Video not available
                                    </button>
                                  )}
                                </div>
                              </div>

                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>

                </div>

              </div>

              {/* Footer */}
              <div className="border-t border-slate-100 pt-4 mt-4 flex justify-end">
                <button
                  onClick={() => setViewStudentModal(null)}
                  className="px-6 py-2 text-sm font-semibold text-slate-600 bg-slate-100 hover:bg-slate-200 rounded-xl transition-colors"
                >
                  Close Panel
                </button>
              </div>

            </div>
          </div>
        );
      })()}

      {/* Video Overlay Sub-modal (Reused) */}
      {activeVideoUrl && (
        <div className="fixed inset-0 bg-black/85 flex items-center justify-center p-4 z-[3000] animate-fadeIn">
          <div className="bg-slate-950 rounded-2xl overflow-hidden max-w-4xl w-full p-4 flex flex-col items-center relative border border-slate-800">
            <button 
              onClick={() => setActiveVideoUrl(null)}
              className="absolute top-4 right-4 text-white hover:text-slate-300 font-bold text-xl z-50 bg-slate-800/80 rounded-full w-8 h-8 flex items-center justify-center"
            >
              ✕
            </button>
            <div className="w-full flex justify-center items-center">
              <video 
                src={getFullUrl(activeVideoUrl)}
                controls 
                autoPlay 
                className="max-h-[75vh] rounded-xl bg-black w-full"
              />
            </div>
            <div className="text-white text-xs mt-3 font-semibold tracking-wide uppercase text-slate-400">
              Evaluation Session Recording Playback
            </div>
          </div>
        </div>
      )}

      {/* View Exam Students List Modal (Reused) */}
      {viewExamStudentsModal && (() => {
        const bundle = viewExamStudentsModal;
        const studentsList = bundle.student_assignments || [];
        
        return (
          <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4 z-[1900] animate-fadeIn">
            <div className="bg-white rounded-2xl max-w-2xl w-full p-6 shadow-xl border border-slate-100 flex flex-col max-h-[80vh]">
              
              {/* Header */}
              <div className="flex justify-between items-start border-b border-slate-100 pb-4 mb-4">
                <div>
                  <h3 className="text-lg font-bold text-slate-900">Exam Student Assignments</h3>
                  <p className="text-xs text-slate-500">
                    Subject: <span className="font-extrabold text-slate-700">{bundle.schedule_details?.subject || '—'}</span> · 
                    Date: <span className="font-semibold text-slate-700">{fmtDate(bundle.schedule_details?.exam_date)}</span>
                  </p>
                </div>
                <button 
                  onClick={() => setViewExamStudentsModal(null)} 
                  className="text-slate-400 hover:text-slate-600 text-lg font-bold w-8 h-8 flex items-center justify-center rounded-full hover:bg-slate-50"
                >
                  ✕
                </button>
              </div>

              {/* Body */}
              <div className="flex-1 overflow-y-auto pr-2 space-y-4">
                {studentsList.length === 0 ? (
                  <div className="text-center py-8 text-slate-400 italic text-sm">
                    No student answer sheets assigned to this exam bundle.
                  </div>
                ) : (
                  <div className="space-y-2.5">
                    {studentsList.map((sa, idx) => {
                      const evals = sa.evaluations || [];
                      const completedCount = evals.filter(ev => ev.status === 'SUBMITTED' || ev.status === 'COMPLETED').length;
                      
                      return (
                        <div 
                          key={sa.id || idx} 
                          className="flex justify-between items-center bg-slate-50 hover:bg-slate-100/60 p-3.5 rounded-xl border border-slate-200/60 transition-colors"
                        >
                          <div className="min-w-0 flex-1">
                            <div className="text-sm font-extrabold text-slate-800 truncate">
                              {idx + 1}. {sa.student_name}
                            </div>
                            <div className="flex items-center gap-2 mt-1 text-xs text-slate-500 font-semibold">
                              <span>REG: {sa.student_id_num || sa.roll_number || '—'}</span>
                              <span>•</span>
                              <span className={completedCount === 3 ? 'text-green-600' : 'text-amber-600'}>
                                Evaluations: {completedCount} / 3 Completed
                              </span>
                            </div>
                          </div>
                          
                          <button
                            onClick={() => handleViewStudentDetails(bundle, sa)}
                            className="bg-[#319ab9] hover:bg-[#28849f] text-white font-bold px-4 py-1.5 rounded-xl text-xs transition-colors shadow-sm"
                          >
                            View Details
                          </button>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Footer */}
              <div className="border-t border-slate-100 pt-4 mt-4 flex justify-end">
                <button
                  onClick={() => setViewExamStudentsModal(null)}
                  className="px-6 py-2 text-sm font-semibold text-slate-600 bg-slate-100 hover:bg-slate-200 rounded-xl transition-colors"
                >
                  Close
                </button>
              </div>

            </div>
          </div>
        );
      })()}
      {/* ── BookletViewer full-screen preview for COE ─────────────────────────── */}
      {coeBookletView && (
        <BookletViewer
          pdfUrl={coeBookletView.pdfUrl}
          studentName={coeBookletView.studentName}
          studentId={coeBookletView.studentId}
          subject={coeBookletView.subject}
          initialAnnotations={coeBookletView.annotations}
          onClose={() => setCoeBookletView(null)}
          readOnly={true}
        />
      )}
    </div>
  );
}

function PracticalMarksTab() {
  const [departments, setDepartments] = useState([]);
  const [schedules, setSchedules] = useState([]);
  const [selectedDept, setSelectedDept] = useState('');
  const [selectedSchedule, setSelectedSchedule] = useState('');
  const [assignments, setAssignments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingSchedules, setLoadingSchedules] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    departmentAPI.getDepartments().then(d => setDepartments(Array.isArray(d) ? d : [])).catch(() => {});
  }, []);

  useEffect(() => {
    if (!selectedDept) {
      setSchedules([]);
      setSelectedSchedule('');
      return;
    }
    setLoadingSchedules(true);
    coeAPI.getSchedules({ department_id: selectedDept })
      .then(d => {
        const list = Array.isArray(d) ? d : (d.results || []);
        const filtered = list.filter(s => (s.sub_type === 'PRACTICAL' || s.exam_type === 'INTERNAL') && String(s.department) === String(selectedDept));
        setSchedules(filtered);
      })
      .catch(() => setSchedules([]))
      .finally(() => setLoadingSchedules(false));
  }, [selectedDept]);

  useEffect(() => {
    if (!selectedSchedule) {
      setAssignments([]);
      return;
    }
    setLoading(true);
    setError('');
    coeAPI.getAllStudentAssignments({ schedule_id: selectedSchedule })
      .then(data => {
        const list = data || [];
        setAssignments(list.filter(asg => String(asg.schedule) === String(selectedSchedule)));
      })
      .catch(err => {
        console.error(err);
        setError('Failed to fetch marks.');
      })
      .finally(() => setLoading(false));
  }, [selectedSchedule]);

  const activeSchObj = schedules.find(s => String(s.id) === String(selectedSchedule));

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20, background: '#fff', padding: 24, borderRadius: 16, border: '1px solid #e5e7eb', boxShadow: '0 4px 20px rgba(0,0,0,0.05)' }}>
      <div>
        <h2 style={{ margin: 0, fontSize: 18, fontWeight: 800, color: '#0f172a' }}>Practical & Internal Marks</h2>
        <p style={{ margin: '4px 0 0 0', fontSize: 13, color: '#64748b' }}>View entered practical, theory, and combined marks submitted by HODs.</p>
      </div>

      <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, flex: 1, minWidth: 200 }}>
          <label style={{ fontSize: 11, fontWeight: 700, color: '#475569', textTransform: 'uppercase' }}>Department</label>
          <select
            value={selectedDept}
            onChange={e => { setSelectedDept(e.target.value); setSelectedSchedule(''); }}
            style={{ padding: '10px 12px', borderRadius: 8, border: '1.5px solid #e2e8f0', fontSize: 13, outline: 'none' }}
          >
            <option value="">— Select Department —</option>
            {departments.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
          </select>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, flex: 2, minWidth: 250 }}>
          <label style={{ fontSize: 11, fontWeight: 700, color: '#475569', textTransform: 'uppercase' }}>Examination Schedule</label>
          {loadingSchedules ? (
            <div style={{ fontSize: 13, color: '#64748b', padding: '10px 0' }}>Loading schedules...</div>
          ) : (
            <select
              value={selectedSchedule}
              onChange={e => setSelectedSchedule(e.target.value)}
              disabled={!selectedDept}
              style={{ padding: '10px 12px', borderRadius: 8, border: '1.5px solid #e2e8f0', fontSize: 13, outline: 'none' }}
            >
              <option value="">— Select Schedule —</option>
              {schedules.map(s => (
                <option key={s.id} value={s.id}>
                  {s.subject} ({s.exam_type_display} · {s.sub_type_display}) — {new Date(s.exam_date).toLocaleDateString('en-IN')}
                </option>
              ))}
            </select>
          )}
        </div>
      </div>

      {error && (
        <div style={{ background: '#fef2f2', border: '1px solid #fecaca', color: '#b91c1c', padding: '12px 16px', borderRadius: '8px', fontSize: 13 }}>
          {error}
        </div>
      )}

      {selectedSchedule && (
        <div style={{ marginTop: 10 }}>
          {loading ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, color: '#64748b', padding: '20px 0' }}>
              <div className="spinner-small"></div> Loading student marks...
            </div>
          ) : (
            <div>
              {activeSchObj && (
                <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 12, padding: 16, marginBottom: 20, display: 'flex', flexWrap: 'wrap', gap: 24 }}>
                  <div>
                    <div style={{ fontSize: '10px', fontWeight: '800', color: '#64748b', textTransform: 'uppercase' }}>Exam Code</div>
                    <div style={{ fontSize: '13px', fontWeight: '700', color: '#0f172a' }}>{activeSchObj.exam_type_display} ({activeSchObj.sub_type_display})</div>
                  </div>
                  <div>
                    <div style={{ fontSize: '10px', fontWeight: '800', color: '#64748b', textTransform: 'uppercase' }}>Subject</div>
                    <div style={{ fontSize: '13px', fontWeight: '700', color: '#0f172a' }}>{activeSchObj.subject}</div>
                  </div>
                  <div>
                    <div style={{ fontSize: '10px', fontWeight: '800', color: '#64748b', textTransform: 'uppercase' }}>Max / Pass Marks</div>
                    <div style={{ fontSize: '13px', fontWeight: '700', color: '#0f172a' }}>{activeSchObj.max_marks} / {activeSchObj.pass_marks}</div>
                  </div>
                </div>
              )}

              {assignments.length === 0 ? (
                <div style={{ textAlign: 'center', padding: 32, background: '#f8fafc', borderRadius: 12, color: '#64748b' }}>
                  No students assigned to this examination.
                </div>
              ) : (
                <div style={{ overflowX: 'auto', border: '1px solid #e2e8f0', borderRadius: 12 }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left' }}>
                    <thead>
                      <tr style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
                        <th style={{ padding: '12px 16px', fontSize: '11px', fontWeight: '700', color: '#475569', textTransform: 'uppercase' }}>Roll Number</th>
                        <th style={{ padding: '12px 16px', fontSize: '11px', fontWeight: '700', color: '#475569', textTransform: 'uppercase' }}>Student Name</th>
                        <th style={{ padding: '12px 16px', fontSize: '11px', fontWeight: '700', color: '#475569', textTransform: 'uppercase' }}>Attendance</th>
                        {activeSchObj?.exam_type === 'INTERNAL' && (
                          <>
                            <th style={{ padding: '12px 16px', fontSize: '11px', fontWeight: '700', color: '#475569', textTransform: 'uppercase' }}>Theory Marks</th>
                            <th style={{ padding: '12px 16px', fontSize: '11px', fontWeight: '700', color: '#475569', textTransform: 'uppercase' }}>Practical Marks</th>
                            <th style={{ padding: '12px 16px', fontSize: '11px', fontWeight: '700', color: '#475569', textTransform: 'uppercase' }}>Combined Total</th>
                          </>
                        )}
                        {activeSchObj?.exam_type !== 'INTERNAL' && (
                          <th style={{ padding: '12px 16px', fontSize: '11px', fontWeight: '700', color: '#475569', textTransform: 'uppercase' }}>Obtained Marks</th>
                        )}
                        <th style={{ padding: '12px 16px', fontSize: '11px', fontWeight: '700', color: '#475569', textTransform: 'uppercase' }}>Status</th>
                        <th style={{ padding: '12px 16px', fontSize: '11px', fontWeight: '700', color: '#475569', textTransform: 'uppercase' }}>Last Entered By</th>
                      </tr>
                    </thead>
                    <tbody>
                      {assignments.map(asg => (
                        <tr key={asg.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                          <td style={{ padding: '12px 16px', fontSize: '13px', fontWeight: '600', color: '#0f172a' }}>{asg.student_id_num || asg.roll_number || '—'}</td>
                          <td style={{ padding: '12px 16px', fontSize: '13px', fontWeight: '600', color: '#0f172a' }}>{asg.student_name}</td>
                          <td style={{ padding: '12px 16px' }}>
                            <span style={{
                              padding: '2px 8px',
                              borderRadius: 12,
                              fontSize: 10,
                              fontWeight: 700,
                              background: asg.attendance === 'PRESENT' ? '#dcfce7' : asg.attendance === 'ABSENT' ? '#fde2e2' : '#f1f5f9',
                              color: asg.attendance === 'PRESENT' ? '#166534' : asg.attendance === 'ABSENT' ? '#991b1b' : '#475569'
                            }}>
                              {asg.attendance}
                            </span>
                          </td>
                          {activeSchObj?.exam_type === 'INTERNAL' && (
                            <>
                              <td style={{ padding: '12px 16px', fontSize: '13px', color: '#334155' }}>
                                {asg.theory_marks !== null && asg.theory_marks !== undefined ? `${asg.theory_marks} / ${asg.max_theory_marks}` : '—'}
                              </td>
                              <td style={{ padding: '12px 16px', fontSize: '13px', color: '#334155' }}>
                                {asg.practical_marks !== null && asg.practical_marks !== undefined ? `${asg.practical_marks} / ${asg.max_practical_marks}` : '—'}
                              </td>
                              <td style={{ padding: '12px 16px', fontSize: '13px', fontWeight: '700', color: '#0f172a' }}>
                                {asg.combined_total !== null && asg.combined_total !== undefined ? `${asg.combined_total} / ${asg.max_combined_marks}` : '—'}
                              </td>
                            </>
                          )}
                          {activeSchObj?.exam_type !== 'INTERNAL' && (
                            <td style={{ padding: '12px 16px', fontSize: '13px', fontWeight: '700', color: '#0f172a' }}>
                              {asg.obtained_marks !== null && asg.obtained_marks !== undefined ? `${asg.obtained_marks} / ${activeSchObj.max_marks}` : '—'}
                            </td>
                          )}
                          <td style={{ padding: '12px 16px' }}>
                            <span style={{
                              padding: '2px 8px',
                              borderRadius: 12,
                              fontSize: 10,
                              fontWeight: 700,
                              background: asg.marks_status === 'SUBMITTED' ? '#dbeafe' : asg.marks_status === 'PENDING' ? '#fef3c7' : '#f1f5f9',
                              color: asg.marks_status === 'SUBMITTED' ? '#1d4ed8' : asg.marks_status === 'PENDING' ? '#92400e' : '#475569'
                            }}>
                              {asg.marks_status === 'SUBMITTED' ? 'Submitted' : asg.marks_status === 'PENDING' ? 'Draft' : 'Not Entered'}
                            </span>
                          </td>
                          <td style={{ padding: '12px 16px', fontSize: '12px', color: '#64748b' }}>
                            {asg.marks_entered_by_name ? (
                              <div>
                                <strong>{asg.marks_entered_by_name}</strong>
                                <div style={{ fontSize: '10px', color: '#94a3b8' }}>
                                  {new Date(asg.marks_entered_at).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}
                                </div>
                              </div>
                            ) : (
                              '—'
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// TAB: Track of Exams
// ══════════════════════════════════════════════════════════════════════════════
function ExamTrackingTab() {
  const [timetables, setTimetables] = useState([]);
  const [selectedTT, setSelectedTT] = useState('');
  const [schedules, setSchedules] = useState([]);
  const [selectedSch, setSelectedSch] = useState('');
  const [assignments, setAssignments] = useState([]);
  const [papers, setPapers] = useState([]);
  const [loadingSch, setLoadingSch] = useState(false);
  const [loadingAsg, setLoadingAsg] = useState(false);

  // Load all timetables and question papers at startup
  useEffect(() => {
    coeAPI.getTimetables()
      .then(d => setTimetables(Array.isArray(d) ? d : (d?.results ?? [])))
      .catch(() => {});
    coeAPI.getQuestionPapers()
      .then(d => setPapers(Array.isArray(d) ? d : (d?.results ?? [])))
      .catch(() => {});
  }, []);

  // When selectedTT changes, fetch schedules for that timetable
  useEffect(() => {
    if (!selectedTT) {
      setSchedules([]);
      setSelectedSch('');
      return;
    }
    setLoadingSch(true);
    coeAPI.getSchedules({ timetable_id: selectedTT })
      .then(d => setSchedules(Array.isArray(d) ? d : (d?.results ?? [])))
      .catch(() => setSchedules([]))
      .finally(() => setLoadingSch(false));
  }, [selectedTT]);

  // When selectedSch changes, fetch assignments for that schedule
  useEffect(() => {
    if (!selectedSch) {
      setAssignments([]);
      return;
    }
    setLoadingAsg(true);
    coeAPI.getAllStudentAssignments({ schedule_id: selectedSch })
      .then(d => setAssignments(Array.isArray(d) ? d : []))
      .catch(() => setAssignments([]))
      .finally(() => setLoadingAsg(false));
  }, [selectedSch]);

  const activeSchObj = schedules.find(s => String(s.id) === String(selectedSch));
  const linkedPaper = papers.find(p => String(p.schedule) === String(selectedSch));

  const fmtDate = (d) => {
    if (!d) return '—';
    return new Date(d).toLocaleDateString('en-IN', { weekday: 'long', day: '2-digit', month: 'short', year: 'numeric' });
  };
  const fmtTime = (t) => {
    if (!t) return '—';
    const [h, m] = t.split(':');
    const hr = parseInt(h);
    return `${hr % 12 || 12}:${m} ${hr >= 12 ? 'PM' : 'AM'}`;
  };

  const getAttendanceBadgeStyle = (att) => {
    if (att === 'PRESENT') return { bg: '#dcfce7', c: '#15803d' };
    if (att === 'ABSENT') return { bg: '#fee2e2', c: '#b91c1c' };
    return { bg: '#f1f5f9', c: '#475569' };
  };

  const getStatusBadgeStyle = (status) => {
    if (status === 'SUBMITTED') return { bg: '#dbeafe', c: '#1d4ed8' };
    if (status === 'PENDING') return { bg: '#fef3c7', c: '#b45309' };
    return { bg: '#f1f5f9', c: '#475569' };
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h2 style={{ margin: 0, fontSize: 18, fontWeight: 800, color: '#0f172a' }}>🔍 Track of Exams</h2>
      </div>

      {/* Select Timetable & Schedule */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 16, background: '#fff', padding: 20, borderRadius: 12, border: '1px solid #e2e8f0' }}>
        <Field label="Select Timetable">
          <select style={inputStyle} value={selectedTT} onChange={e => { setSelectedTT(e.target.value); setSelectedSch(''); }}>
            <option value="">— Choose Timetable —</option>
            {timetables.map(t => <option key={t.id} value={t.id}>{t.title}</option>)}
          </select>
        </Field>
        <Field label="Select Exam Schedule">
          <select style={inputStyle} value={selectedSch} onChange={e => setSelectedSch(e.target.value)} disabled={!selectedTT || loadingSch}>
            {loadingSch ? (
              <option>Loading schedules...</option>
            ) : (
              <>
                <option value="">— Choose Schedule —</option>
                {schedules.map(s => {
                  const typeLabel = s.exam_type === 'UNIVERSITY'
                    ? (s.sub_type === 'THEORY' ? 'Univ Theory' : 'Univ Practical')
                    : (s.exam_type === 'INTERNAL'
                      ? (s.sub_type === 'THEORY' ? 'Int Theory' : 'Int Practical')
                      : `${s.exam_type_display} ${s.sub_type_display}`);
                  return (
                    <option key={s.id} value={s.id}>
                      {s.paper_label || s.subject} ({typeLabel}) — {fmtDate(s.exam_date)}
                    </option>
                  );
                })}
              </>
            )}
          </select>
        </Field>
      </div>

      {activeSchObj && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
          
          {/* Main Column: Student Marks, Attendance, Audit History */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
            
            {/* Exam Schedule Summary */}
            <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: 20 }}>
              <h3 style={{ margin: '0 0 16px 0', fontSize: 15, fontWeight: 800, color: '#0f172a', borderBottom: '1px solid #f1f5f9', paddingBottom: 10 }}>
                📋 Schedule Details
              </h3>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 16 }}>
                {[
                  ['Subject', activeSchObj.subject],
                  ['Department', activeSchObj.department_name],
                  ['Date', fmtDate(activeSchObj.exam_date)],
                  ['Time', `${fmtTime(activeSchObj.start_time)} – ${fmtTime(activeSchObj.end_time)}`],
                  ['Venue', activeSchObj.venue || 'TBA'],
                  ['Max / Pass Marks', `${activeSchObj.max_marks} / ${activeSchObj.pass_marks}`],
                  ['Exam Type', `${activeSchObj.exam_type_display} (${activeSchObj.sub_type_display})`],
                  ['Assigned Students', `${assignments.length} Student(s)`]
                ].map(([lbl, val]) => (
                  <div key={lbl}>
                    <div style={{ fontSize: 11, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase' }}>{lbl}</div>
                    <div style={{ fontSize: 13, fontWeight: 600, color: '#334155', marginTop: 4 }}>{val}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* Students Table */}
            <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, overflow: 'hidden' }}>
              <div style={{ padding: '16px 20px', borderBottom: '1px solid #f1f5f9', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <h3 style={{ margin: 0, fontSize: 15, fontWeight: 800, color: '#0f172a' }}>
                  🎓 Assigned Students, Attendance & Results
                </h3>
              </div>
              
              {loadingAsg ? (
                <div style={{ padding: 40, textAlign: 'center', color: '#64748b' }}>Loading assignments...</div>
              ) : assignments.length === 0 ? (
                <div style={{ padding: 40, textAlign: 'center', color: '#64748b' }}>No students assigned to this schedule.</div>
              ) : (
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left' }}>
                    <thead>
                      <tr style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
                        <th style={{ padding: '14px 16px', fontSize: 11, fontWeight: 700, color: '#475569', textTransform: 'uppercase', whiteSpace: 'nowrap', minWidth: '130px' }}>Roll No / ID</th>
                        <th style={{ padding: '14px 16px', fontSize: 11, fontWeight: 700, color: '#475569', textTransform: 'uppercase', whiteSpace: 'nowrap', minWidth: '160px' }}>Student Name</th>
                        <th style={{ padding: '14px 16px', fontSize: 11, fontWeight: 700, color: '#475569', textTransform: 'uppercase', whiteSpace: 'nowrap', minWidth: '120px' }}>Attendance</th>
                        {activeSchObj.exam_type === 'INTERNAL' ? (
                          <>
                            <th style={{ padding: '14px 16px', fontSize: 11, fontWeight: 700, color: '#475569', textTransform: 'uppercase', whiteSpace: 'nowrap', minWidth: '90px' }}>Theory</th>
                            <th style={{ padding: '14px 16px', fontSize: 11, fontWeight: 700, color: '#475569', textTransform: 'uppercase', whiteSpace: 'nowrap', minWidth: '90px' }}>Practical</th>
                            <th style={{ padding: '14px 16px', fontSize: 11, fontWeight: 700, color: '#475569', textTransform: 'uppercase', whiteSpace: 'nowrap', minWidth: '100px' }}>Combined</th>
                          </>
                        ) : activeSchObj.exam_type === 'UNIVERSITY' && activeSchObj.sub_type === 'THEORY' ? (
                          <>
                            <th style={{ padding: '14px 16px', fontSize: 11, fontWeight: 700, color: '#475569', textTransform: 'uppercase', whiteSpace: 'nowrap', minWidth: '190px' }}>Avg Marks (Evaluators)</th>
                            <th style={{ padding: '14px 16px', fontSize: 11, fontWeight: 700, color: '#475569', textTransform: 'uppercase', whiteSpace: 'nowrap', minWidth: '280px' }}>Evaluator Breakdown</th>
                          </>
                        ) : (
                          <th style={{ padding: '14px 16px', fontSize: 11, fontWeight: 700, color: '#475569', textTransform: 'uppercase', whiteSpace: 'nowrap', minWidth: '120px' }}>Marks</th>
                        )}
                        <th style={{ padding: '14px 16px', fontSize: 11, fontWeight: 700, color: '#475569', textTransform: 'uppercase', whiteSpace: 'nowrap', minWidth: '130px' }}>Status</th>
                        <th style={{ padding: '14px 16px', fontSize: 11, fontWeight: 700, color: '#475569', textTransform: 'uppercase', whiteSpace: 'nowrap', minWidth: '190px' }}>Auditor / Timestamp</th>
                      </tr>
                    </thead>
                    <tbody>
                      {assignments.map(asg => {
                        const attBadge = getAttendanceBadgeStyle(asg.attendance);
                        const statusBadge = getStatusBadgeStyle(asg.marks_status);
                        const pe = asg.paper_evaluations || { evaluations: [], avg_marks: null, submitted_count: 0, total_evaluators: 0 };
                        const isUnivTheory = activeSchObj.exam_type === 'UNIVERSITY' && activeSchObj.sub_type === 'THEORY';
                        return (
                          <tr key={asg.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                            <td style={{ padding: '14px 16px', fontSize: 12, fontWeight: 600, color: '#0f172a', whiteSpace: 'nowrap' }}>{asg.student_id_num || asg.roll_number || '—'}</td>
                            <td style={{ padding: '14px 16px', fontSize: 12, fontWeight: 600, color: '#334155', whiteSpace: 'nowrap' }}>{asg.student_name}</td>
                            <td style={{ padding: '14px 16px', whiteSpace: 'nowrap' }}>
                              <span style={{ padding: '2px 8px', borderRadius: 12, fontSize: 10, fontWeight: 700, background: attBadge.bg, color: attBadge.c }}>
                                {asg.attendance || 'NOT MARKED'}
                              </span>
                            </td>
                            {activeSchObj.exam_type === 'INTERNAL' ? (
                              <>
                                <td style={{ padding: '14px 16px', fontSize: 12, color: '#334155', whiteSpace: 'nowrap' }}>
                                  {asg.theory_marks !== null && asg.theory_marks !== undefined ? `${asg.theory_marks} / ${asg.max_theory_marks || '—'}` : '—'}
                                </td>
                                <td style={{ padding: '14px 16px', fontSize: 12, color: '#334155', whiteSpace: 'nowrap' }}>
                                  {asg.practical_marks !== null && asg.practical_marks !== undefined ? `${asg.practical_marks} / ${asg.max_practical_marks || '—'}` : '—'}
                                </td>
                                <td style={{ padding: '14px 16px', fontSize: 12, fontWeight: 700, color: '#0f172a', whiteSpace: 'nowrap' }}>
                                  {asg.combined_total !== null && asg.combined_total !== undefined ? `${asg.combined_total} / ${asg.max_combined_marks || '—'}` : '—'}
                                </td>
                              </>
                            ) : isUnivTheory ? (
                              <>
                                {/* Avg marks from paper evaluators */}
                                <td style={{ padding: '14px 16px', fontSize: 12, fontWeight: 700, color: '#0f172a', whiteSpace: 'nowrap' }}>
                                  {pe.avg_marks !== null && pe.avg_marks !== undefined
                                    ? <span style={{ fontWeight: 800 }}>{pe.avg_marks} <span style={{ fontSize: 10, color: '#64748b', fontWeight: 500 }}>/ {activeSchObj.max_marks} ({pe.submitted_count}/{pe.total_evaluators} submitted)</span></span>
                                    : <span style={{ color: '#94a3b8' }}>Pending</span>}
                                </td>
                                {/* Per-evaluator breakdown */}
                                <td style={{ padding: '12px 16px' }}>
                                  {pe.evaluations.length === 0 ? (
                                    <span style={{ fontSize: 11, color: '#94a3b8' }}>No evaluators assigned</span>
                                  ) : (
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                                      {pe.evaluations.map((ev, idx) => (
                                        <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, whiteSpace: 'nowrap' }}>
                                          <span style={{
                                            padding: '1px 6px', borderRadius: 8, fontSize: 10, fontWeight: 700,
                                            background: ev.status === 'SUBMITTED' ? '#dcfce7' : ev.status === 'SAVED' ? '#fef3c7' : '#f1f5f9',
                                            color: ev.status === 'SUBMITTED' ? '#15803d' : ev.status === 'SAVED' ? '#b45309' : '#64748b',
                                            whiteSpace: 'nowrap'
                                          }}>{ev.status}</span>
                                          <span style={{ color: '#334155', fontWeight: 600, whiteSpace: 'nowrap' }}>{ev.evaluator_name}</span>
                                          <span style={{ color: ev.status === 'SUBMITTED' ? '#15803d' : '#94a3b8', fontWeight: 700, whiteSpace: 'nowrap' }}>
                                            {ev.status === 'SUBMITTED' ? `${ev.total_marks} M` : '—'}
                                          </span>
                                        </div>
                                      ))}
                                    </div>
                                  )}
                                </td>
                              </>
                            ) : (
                              <td style={{ padding: '14px 16px', fontSize: 12, fontWeight: 700, color: '#0f172a', whiteSpace: 'nowrap' }}>
                                {asg.obtained_marks !== null && asg.obtained_marks !== undefined ? `${asg.obtained_marks} / ${activeSchObj.max_marks}` : '—'}
                              </td>
                            )}
                            <td style={{ padding: '14px 16px', whiteSpace: 'nowrap' }}>
                              <span style={{ padding: '2px 8px', borderRadius: 12, fontSize: 10, fontWeight: 700, background: statusBadge.bg, color: statusBadge.c }}>
                                {isUnivTheory
                                  ? (pe.submitted_count === pe.total_evaluators && pe.total_evaluators > 0 ? 'All Evaluated' : pe.submitted_count > 0 ? `${pe.submitted_count}/${pe.total_evaluators} Done` : 'Pending')
                                  : (asg.marks_status === 'SUBMITTED' ? 'Submitted' : asg.marks_status === 'PENDING' ? 'Draft' : 'Not Entered')}
                              </span>
                            </td>
                            <td style={{ padding: '14px 16px', fontSize: 11, color: '#64748b', whiteSpace: 'nowrap' }}>
                              {isUnivTheory ? (
                                pe.evaluations.filter(e => e.status === 'SUBMITTED' && e.evaluated_at).length > 0 ? (
                                  <div>
                                    <div style={{ fontWeight: 600 }}>Last: {pe.evaluations.filter(e => e.status === 'SUBMITTED').slice(-1)[0]?.evaluator_name}</div>
                                    <div style={{ fontSize: 9 }}>{new Date(pe.evaluations.filter(e => e.status === 'SUBMITTED').slice(-1)[0]?.evaluated_at).toLocaleString('en-IN')}</div>
                                  </div>
                                ) : '—'
                              ) : asg.marks_entered_by_name ? (
                                <div>
                                  <div style={{ fontWeight: 600 }}>{asg.marks_entered_by_name}</div>
                                  <div style={{ fontSize: 9 }}>{new Date(asg.marks_entered_at).toLocaleString('en-IN')}</div>
                                </div>
                              ) : '—'}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

          </div>

          {/* Bottom Section: Question Paper Details */}
          <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: 20 }}>
            <h3 style={{ margin: '0 0 16px 0', fontSize: 15, fontWeight: 800, color: '#0f172a', borderBottom: '1px solid #f1f5f9', paddingBottom: 10, display: 'flex', alignItems: 'center', gap: 6 }}>
              <span>📝 Question Paper Details</span>
            </h3>
            
            {linkedPaper ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
                  <div>
                    <div style={{ fontSize: 11, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase' }}>Paper Title</div>
                    <div style={{ fontSize: 14, fontWeight: 700, color: '#1e293b', marginTop: 4 }}>{linkedPaper.title}</div>
                  </div>
                  <div>
                    <div style={{ fontSize: 11, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase' }}>Duration</div>
                    <div style={{ fontSize: 13, fontWeight: 600, color: '#334155', marginTop: 4 }}>{linkedPaper.duration_minutes} Mins</div>
                  </div>
                </div>
                {linkedPaper.instructions && (
                  <div>
                    <div style={{ fontSize: 11, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase' }}>Instructions</div>
                    <div style={{ fontSize: 12, color: '#475569', marginTop: 4, whiteSpace: 'pre-line' }}>{linkedPaper.instructions}</div>
                  </div>
                )}
                
                {linkedPaper.sections && linkedPaper.sections.length > 0 && (
                  <div style={{ marginTop: 10 }}>
                    <div style={{ fontSize: 11, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', marginBottom: 12 }}>Sections & Questions</div>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: 16 }}>
                      {linkedPaper.sections.map((sec, idx) => (
                        <div key={sec.id} style={{ background: '#f8fafc', padding: 16, borderRadius: 10, border: '1px solid #e2e8f0', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                          <div>
                            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, fontWeight: 700, color: '#0f172a' }}>
                              <span>Section {idx + 1}: {sec.title}</span>
                              <span style={{ color: ACCENT }}>{sec.marks_per_q * (sec.attempt_questions || sec.total_questions)} M</span>
                            </div>
                            <div style={{ fontSize: 11, color: '#64748b', marginTop: 4, marginBottom: 10 }}>
                              Answer any {sec.attempt_questions || sec.total_questions} of {sec.total_questions} (Each {sec.marks_per_q}M)
                            </div>
                            {sec.questions && sec.questions.length > 0 && (
                              <ol style={{ margin: '8px 0 0 16px', padding: 0, fontSize: 12, color: '#334155' }}>
                                {sec.questions.map(q => (
                                  <li key={q.id} style={{ marginBottom: 6 }}>
                                    <div>{q.text} <span style={{ color: '#64748b', fontSize: 10, fontWeight: 600 }}>({q.marks}M)</span></div>
                                    {q.options && q.options.length > 0 && (
                                      <ul style={{ listStyleType: 'lower-alpha', paddingLeft: 14, margin: '4px 0', color: '#64748b' }}>
                                        {q.options.map((opt, oIdx) => <li key={oIdx}>{opt}</li>)}
                                      </ul>
                                    )}
                                  </li>
                                ))}
                              </ol>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div style={{ padding: '30px 10px', textAlign: 'center', color: '#94a3b8', background: '#f8fafc', borderRadius: 8, border: '1px dashed #cbd5e1' }}>
                <span style={{ fontSize: 32, display: 'block', marginBottom: 8 }}>📭</span>
                No question paper uploaded for this exam schedule.
              </div>
            )}
          </div>

        </div>
      )}

      {!selectedSch && (
        <div style={{ textAlign: 'center', padding: '60px 20px', color: '#94a3b8', background: '#fff', borderRadius: 12, border: '1px solid #e2e8f0' }}>
          <span style={{ fontSize: 36, display: 'block', marginBottom: 10 }}>🔍</span>
          <div style={{ fontWeight: 700, fontSize: 14, color: '#475569' }}>Please select a Timetable and Exam Schedule to view histories.</div>
        </div>
      )}
    </div>
  );
}

const TABS = [
  { key: 'overview',            label: '🏠 Dashboard' },
  { key: 'timetables',          label: '📅 Timetables' },
  { key: 'schedules',           label: '📋 Schedules' },
  { key: 'students',            label: '🎓 Students' },
  { key: 'answer-sheets',       label: '📄 Answer Sheets' },
  { key: 'question-papers',     label: '📝 Question Papers' },
  { key: 'thesis',              label: '🎓 Thesis Evaluation' },
  { key: 'evaluator-approvals', label: '🛡️ Evaluator Approvals' },
  { key: 'assign-sheets',       label: '📦 Assign Answer Sheets' },
  { key: 'practical-marks',     label: '📊 Practical & Internal Marks' },
  { key: 'exam-tracking',       label: '🔍 Track of Exams' },
  { key: 'calendar',            label: '📅 Calendar' },
];

const COEDashboard = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('overview');
  const [stats,     setStats]     = useState({});

  useEffect(() => {
    coeAPI.getStats().then(setStats).catch(() => {});
  }, []);

  return (
    <DashboardLayout title={`COE — ${user?.first_name || 'Controller of Examinations'}`}>
      <div style={{ fontFamily: "'DM Sans','Segoe UI',sans-serif", display: 'flex', flexDirection: 'column', gap: 0, minHeight: '100vh', background: '#f8fafc' }}>

        {/* Tab bar */}
        <div style={{ background: '#fff', borderBottom: '1px solid #e5e7eb', padding: '0 20px', overflowX: 'auto' }}>
          <div style={{ display: 'flex', gap: 0, minWidth: 'max-content' }}>
            {TABS.map(t => (
              <button key={t.key} onClick={() => setActiveTab(t.key)}
                style={{
                  padding: '14px 18px',
                  border: 'none',
                  borderBottom: activeTab === t.key ? `3px solid ${ACCENT}` : '3px solid transparent',
                  background: 'none',
                  color: activeTab === t.key ? ACCENT : '#374151',
                  fontWeight: activeTab === t.key ? 700 : 500,
                  fontSize: 13,
                  cursor: 'pointer',
                  fontFamily: 'inherit',
                  whiteSpace: 'nowrap',
                  transition: 'color .15s',
                }}>
                {t.label}
              </button>
            ))}
          </div>
        </div>

        {/* Tab content */}
        <div style={{ padding: '24px 20px', flex: 1 }}>
          {activeTab === 'overview'            && <OverviewTab stats={stats} onNavigate={setActiveTab} />}
          {activeTab === 'timetables'          && <TimetablesTab />}
          {activeTab === 'schedules'           && <SchedulesTab />}
          {activeTab === 'students'            && <StudentsTab />}
          {activeTab === 'answer-sheets'       && <AnswerSheetsTab />}
          {activeTab === 'question-papers'     && <QuestionPapersTab />}
          {activeTab === 'thesis'              && <ThesisEvaluationTab />}
          {activeTab === 'evaluator-approvals' && <EvaluatorApprovalsTab />}
          {activeTab === 'assign-sheets'       && <AssignAnswerSheetsTab />}
          {activeTab === 'practical-marks'     && <PracticalMarksTab />}
          {activeTab === 'exam-tracking'       && <ExamTrackingTab />}
          {activeTab === 'calendar'            && <div style={{ background: '#fff', borderRadius: 12, border: '1px solid #e2e8f0', padding: 20 }}><Calendar /></div>}
        </div>
      </div>
    </DashboardLayout>
  );
};

export default COEDashboard;